import React from "react";
import { useState, useEffect, useRef, useMemo, useLayoutEffect } from "react";
import { usePlaidLink } from "react-plaid-link";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, query, where, getDocs, limit } from "firebase/firestore";
import { db } from "./firebase";
import {
  loadBudgetDocs,
  loadClassifiedRows,
  sumTotals,
  computeBVA,
  monthKeysBetween,
  groupMonthly,
} from "./lib/reportingCore";
import { currentMonthRange, last3FullMonthsCombined } from "./lib/dateRanges";
import { last3FullMonthsRange } from "./lib/dateRanges";
import { getSubcategoryStatus } from "./getSubcategoryStatus";
import { getSubcategoryStatus as subStatus } from "./getSubcategoryStatus";
import TimeBadge from "./lib/TimeBadge.jsx";

import TransactionRowStyled from "./TransactionRowStyled";
import { getSuggestedCategory } from "./getSuggestedCategory";
import RuleConfirmationModal from "./RuleConfirmationModal";
import RuleCreator from "./RuleCreator";
import { saveUserRule } from "./ruleMatcher";
import { useNavigate, useLocation } from "react-router-dom";
import { doc, updateDoc, deleteDoc, writeBatch, getDoc, onSnapshot, serverTimestamp, limit as fsLimit } from "firebase/firestore";
import { createPortal } from "react-dom";
import PlaidLinkButton from "./PlaidLinkButton";

// --- DEBUG --------------------------------------------------------------
// NOTE: This file had render-level console.logs that can drown out action logs.
// Use these flags to control signal/noise while debugging desktop menu/modal flow.
const DEBUG_NEEDS = false;  // verbose row-level logging (very noisy)
const DEBUG_ACTIONS = true; // focused action tracing

const traceNA = (step, data = {}) => {
  if (!DEBUG_ACTIONS) return;
  try {
    // Timestamp makes logs readable even under render churn
    console.log(`[NA-TRACE] ${performance.now().toFixed(1)} ${step}`, data);
  } catch {
    console.log(`[NA-TRACE] ${step}`, data);
  }
};
// ----------------------------------------------------------------------

// Trelli colors & ramps (same as Reports)
const TRELLI_GREEN = "#00B074";
const TRELLI_RED   = "#B00020";
const TRELLI_GOLD  = "#F4C542";
const GREEN_SHADES = ["#004E3A", "#006A4F", "#008764", "#00A579", "#00B074", "#33C092", "#66D0AF", "#99E1CB"];
const RED_SHADES   = ["#5E0010", "#7D0016", "#9C001C", "#B00020", "#C53A4A", "#D8646D", "#E98E90", "#F0B6B8"];
const GOLD_SHADES  = ["#7D5A00", "#9C7400", "#BA8E00", "#D5A61D", "#F4C542", "#F7D268", "#FAE090", "#FCE9B2"];
const hashLabel = (s) => Array.from(String(s)).reduce((acc, ch) => (acc * 33 + ch.charCodeAt(0)) >>> 0, 0);
const incomeColorFor  = (name) => GREEN_SHADES[hashLabel(name) % GREEN_SHADES.length];
const expenseColorFor = (name) => RED_SHADES[hashLabel(name) % RED_SHADES.length];
const savingsColorFor = (name) => GOLD_SHADES[hashLabel(name) % GOLD_SHADES.length];

// TEMP: force-show “Needs Action” even if empty
const SHOW_NEEDS_ACTION_DEBUG = false; // ← set to false later

// Small gold warning icon (outlined triangle with exclamation)
const AlertIcon = ({ title = "Alert" }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-label={title} role="img">
    {/* Triangle outline */}
    <path
      d="M12 3 L22 21 H2 Z"
      fill="none"
      stroke={TRELLI_GOLD}
      strokeWidth="2"
      strokeLinejoin="round"
    />
    {/* Exclamation mark */}
    <line x1="12" y1="9" x2="12" y2="14" stroke={TRELLI_GOLD} strokeWidth="2" strokeLinecap="round" />
    <circle cx="12" cy="17" r="1.2" fill={TRELLI_GOLD} />
  </svg>
);

// simple currency helper (same style you use on Reports)
const toUSD = (n) =>
  (Number(n) || 0).toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  });

// robust date formatter for Safari/iOS — tolerates undefined and Firestore Timestamp
const formatDate = (d) => {
  try {
    const dt =
      d && typeof d.toDate === "function" ? d.toDate()
      : d instanceof Date ? d
      : d ? new Date(d)
      : null;
    if (!dt || isNaN(dt.getTime())) return "—";
    return dt.toLocaleDateString();
  } catch {
    return "—";
  }
};

// --- Net Summary cards as small helpers (so we can re-order on mobile) ---
const NetSummaryMTD = ({ cmTotals, cmExpenseOnly, cmSavingsOnly, loading }) => (
  <div className="dash-card" style={{ ...cardStyle, maxWidth: "100%", overflowX: "hidden" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
      <div style={{ fontSize: 14, color: "#666" }}>Net Summary</div>
      <TimeBadge basis="current" />
    </div>
    <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Income</div>
        <div style={{ fontWeight: 700, color: TRELLI_GREEN }}>{toUSD(cmTotals.income)}</div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Expenses</div>
        <div style={{ fontWeight: 700, color: TRELLI_RED }}>{toUSD(cmExpenseOnly)}</div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Savings/Goals</div>
        <div style={{ fontWeight: 700, color: TRELLI_GOLD }}>{toUSD(cmSavingsOnly)}</div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Net</div>
        <div style={{ fontWeight: 700, color: "#111" }}>{toUSD(cmTotals.net)}</div>
      </div>
    </div>
    {loading && <div style={{ marginTop: 8, fontSize: 12, color: "#999" }}>Loading…</div>}
  </div>
);

const NetSummaryL3 = ({ l3IncomeSum, l3ExpenseSum, l3SavingsSum, l3NetSum, loading }) => (
  <div className="dash-card" style={{ ...cardStyle, maxWidth: "100%", overflowX: "hidden" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
      <div style={{ fontSize: 14, color: "#666" }}>Net Summary</div>
      <TimeBadge basis="last3" />
    </div>
    <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Income</div>
        <div style={{ fontWeight: 700, color: TRELLI_GREEN }}>{toUSD(l3IncomeSum)}</div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Expenses</div>
        <div style={{ fontWeight: 700, color: TRELLI_RED }}>{toUSD(l3ExpenseSum)}</div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Savings/Goals</div>
        <div style={{ fontWeight: 700, color: TRELLI_GOLD }}>{toUSD(l3SavingsSum)}</div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Net</div>
        <div style={{ fontWeight: 700, color: "#111" }}>{toUSD(l3NetSum)}</div>
      </div>
    </div>
    {loading && <div style={{ marginTop: 8, fontSize: 12, color: "#999" }}>Loading…</div>}
  </div>
);

// --- Net Summary (MOBILE toggle) ---
const NetSummaryMobileToggle = ({
  cmTotals,
  cmExpenseOnly,
  cmSavingsOnly,
  l3IncomeSum,
  l3ExpenseSum,
  l3SavingsSum,
  l3NetSum,
  loading
}) => {
  const [tab, setTab] = useState("current"); // "current" | "last3"

  const pills = [
    { key: "current", label: "Current Month", color: TRELLI_GREEN, bg: "#F2FBF7" }, // light green
    { key: "last3",  label: "Last 3 Full Months", color: TRELLI_GOLD,  bg: "#FFF7DA" }, // light gold
  ];

  const values = tab === "current"
    ? {
        income: Number(cmTotals?.income || 0),
        expense: Number(cmExpenseOnly || 0),
        savings: Number(cmSavingsOnly || 0),
        net: Number(cmTotals?.net || 0),
        badge: "current",
      }
    : {
        income: Number(l3IncomeSum || 0),
        expense: Number(l3ExpenseSum || 0),
        savings: Number(l3SavingsSum || 0),
        net: Number(l3NetSum || 0),
        badge: "last3",
      };

  return (
    <div className="dash-card" style={{ ...cardStyle, maxWidth: "100%", overflowX: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 14, color: "#666" }}>Net Summary</div>
      </div>

      {/* Pills row — centered */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 8,
          margin: "8px 0",
          flexWrap: "wrap"
        }}
      >
        {pills.map((p) => {
          const selected = tab === p.key;
          return (
            <button
              key={p.key}
              onClick={() => setTab(p.key)}
              style={{
                borderRadius: 999,
                padding: "6px 12px",
                border: `1px solid ${selected ? p.color : "#ddd"}`,
                background: selected ? (p.key === "current" ? TRELLI_GREEN : TRELLI_GOLD) : "#fff",
                color: selected ? (p.key === "current" ? "#fff" : "#111") : p.color,
                fontWeight: 700,
                lineHeight: 1,
                cursor: "pointer",
                minWidth: 0
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Income</div>
          <div style={{ fontWeight: 700, color: TRELLI_GREEN }}>{toUSD(values.income)}</div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Expenses</div>
          <div style={{ fontWeight: 700, color: TRELLI_RED }}>{toUSD(values.expense)}</div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Savings/Goals</div>
          <div style={{ fontWeight: 700, color: TRELLI_GOLD }}>{toUSD(values.savings)}</div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Net</div>
          <div style={{ fontWeight: 700, color: "#111" }}>{toUSD(values.net)}</div>
        </div>
      </div>

      {loading && <div style={{ marginTop: 8, fontSize: 12, color: "#999" }}>Loading…</div>}
    </div>
  );
};

// --- Budget vs Actual (MOBILE toggle wrapper) ---
// --- Budget vs Actual (MOBILE toggle wrapper) ---
const BudgetVsActualMobileToggle = ({ children }) => {
  // children[0] = Current Month component
  // children[1] = Last 3 Full Months component
  const [tab, setTab] = useState("current"); // "current" | "last3"

  const pills = [
    { key: "current", label: "Current Month", color: TRELLI_GREEN, bg: "#F2FBF7" }, // light green
    { key: "last3",  label: "Last 3 Full Months", color: TRELLI_GOLD,  bg: "#FFF7DA" }, // light gold
  ];

  const badge = tab === "current" ? "current" : "last3";
  const child = tab === "current" ? children?.[0] : children?.[1];

  // Clone the selected child to hide its own header/card chrome
  const selectedChild = child
    ? React.cloneElement(child, { noChrome: true, noHeader: true, isMobile: true })
    : null;

  return (
    <div className="dash-card" style={{ ...cardStyle, maxWidth: "100%", overflowX: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 14, color: "#666" }}>Budget vs Actual</div>
      </div>

      {/* Pills row — centered */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 8,
          margin: "8px 0",
          flexWrap: "wrap"
        }}
      >
        {pills.map((p) => {
          const selected = tab === p.key;
          return (
            <button
              key={p.key}
              onClick={() => setTab(p.key)}
              style={{
                borderRadius: 999,
                padding: "6px 12px",
                border: `1px solid ${selected ? p.color : "#ddd"}`,
                background: selected ? (p.key === "current" ? TRELLI_GREEN : TRELLI_GOLD) : "#fff",
                color: selected ? (p.key === "current" ? "#fff" : "#111") : p.color,
                fontWeight: 700,
                lineHeight: 1,
                cursor: "pointer",
                minWidth: 0
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Render the child without its own card/header */}
      <div style={{ marginTop: 8 }}>
        {selectedChild}
      </div>
    </div>
  );
};

// --- Budget Beats & Misses (MOBILE toggle) ---
const BeatsMissesMobileToggle = ({ beats, isMobile }) => {
  const [tab, setTab] = useState("beats"); // "beats" | "misses"

  const pills = [
    { key: "beats",  label: "Beats",  color: TRELLI_GREEN, bg: "#F2FBF7" }, // light green
    { key: "misses", label: "Misses", color: TRELLI_RED,   bg: "#FFF5F5" }, // light red
  ];

  // Build rows for the selected tab (same sources as BeatsCard / MissesCard)
  const rows = tab === "beats"
    ? [ ...(beats?.beatCatsTop || []), ...(beats?.beatSubsTop || []) ]
    : [ ...(beats?.missCatsTop || []), ...(beats?.missSubsTop || []) ];

  // Match the color wash for the list on each tab
  const listColor = tab === "beats" ? TRELLI_GREEN : TRELLI_RED;

  return (
    <div className="dash-card" style={{ ...cardStyle, maxWidth: "100%", overflowX: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 14, color: "#666" }}>Budget Beats &amp; Misses</div>
        <TimeBadge basis="current" />
      </div>

      {/* Pills row — centered */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 8,
          margin: "8px 0",
          flexWrap: "wrap"
        }}
      >
        {pills.map((p) => {
          const selected = tab === p.key;
          return (
            <button
              key={p.key}
              onClick={() => setTab(p.key)}
              style={{
                borderRadius: 999,
                padding: "6px 12px",
                border: `1px solid ${selected ? p.color : "#ddd"}`,
                background: selected ? p.bg : "#fff", // white when not selected
                color: p.color,                        // green or red text
                fontWeight: 700,
                lineHeight: 1,
                cursor: "pointer",
                minWidth: 0
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* The list content for the selected tab */}
      <div style={{ marginTop: 8 }}>
        <ListCard
          // re-use your lightweight list renderer so we don’t nest cards
          title={tab === "beats" ? "Budget Beats" : "Budget Misses"}
          badge={null}
          rows={rows}
          color={listColor}
          isMobile={isMobile}
        />
      </div>
    </div>
  );
};

// --- Top Movers (MOBILE toggle) ---
const MoversMobileToggle = ({ movers, isMobile }) => {
  const [tab, setTab] = useState("up"); // "up" (favorably) | "down" (unfavorably)

  const pills = [
    { key: "up",   label: "Favorably",   color: TRELLI_GREEN, bg: "#F2FBF7" }, // light green
    { key: "down", label: "Unfavorably", color: TRELLI_RED,   bg: "#FFF5F5" }, // light red
  ];

  // rows mirror MoversUpCard / MoversDownCard but combined for mobile
  const rows = tab === "up"
    ? [
        ...(movers?.catsImprovingTop || []).map(r => ({ label: r.label, amount: Math.abs(r.slope) })),
        ...(movers?.subsImprovingTop || []).map(r => ({ label: r.label, amount: Math.abs(r.slope) })),
      ]
    : [
        ...(movers?.catsWorseningTop || []).map(r => ({ label: r.label, amount: Math.abs(r.slope) })),
        ...(movers?.subsWorseningTop || []).map(r => ({ label: r.label, amount: Math.abs(r.slope) })),
      ];

  const listColor = tab === "up" ? TRELLI_GREEN : TRELLI_RED;

  return (
    <div className="dash-card" style={{ ...cardStyle, maxWidth: "100%", overflowX: "hidden" }}>
      {/* Title with single badge like Net Summary / BvA */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 14, color: "#666" }}>Top Movers</div>
        <TimeBadge basis="last3" />
      </div>

      {/* Pills */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 8,
          margin: "8px 0",
          flexWrap: "wrap"
        }}
      >
        {pills.map((p) => {
          const selected = tab === p.key;
          return (
            <button
              key={p.key}
              onClick={() => setTab(p.key)}
              style={{
                borderRadius: 999,
                padding: "6px 12px",
                border: `1px solid ${selected ? p.color : "#ddd"}`,
                background: selected ? p.bg : "#fff",
                color: p.color,
                fontWeight: 700,
                lineHeight: 1,
                cursor: "pointer",
                minWidth: 0
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* List content (no inner badge/title to avoid duplicates) */}
      <div style={{ marginTop: 8 }}>
        <ListCard
          title={tab === "up" ? "Top Movers — Favorably" : "Top Movers — Unfavorably"}
          badge={null}
          rows={rows}
          color={listColor}
          isMobile={isMobile}
        />
      </div>
    </div>
  );
};

// --- Budget vs Actual cards (so we can re-order on mobile) ---
const BvaCard = ({ title = "Budget vs Actual", badge = "current", bva, loading, isMobile = false, noChrome = false, noHeader = false }) => {
  // Header for the 2nd column:
  // - Current month (badge="current"): "MTD Budgeted"
  // - Last 3 full months (badge="last3"): "Budgeted"
  const budgetHeader = badge === "last3" ? "Budgeted" : "MTD Budgeted";

  const hdrNum = {
    ...thTdPad,
    textAlign: isMobile ? "right" : "left",
    paddingRight: isMobile ? 4 : 0,
    fontVariantNumeric: "tabular-nums"
  };

  const numCell = {
    ...thTdPad,
    textAlign: isMobile ? "right" : "left",
    fontVariantNumeric: "tabular-nums"
  };

  const tableBlock = (
    <>
      <div style={{ marginTop: 10 }}>
        <table style={tableStyle}>
          <colgroup>
            {/* Label column */}
            <col style={{ width: isMobile ? "34%" : "28%" }} />
            {/* Budgeted */}
            <col style={{ width: isMobile ? "33%" : "24%" }} />
            {/* Actual */}
            <col style={{ width: isMobile ? "33%" : "24%" }} />
            {/* Variance (desktop only) */}
            {!isMobile && <col style={{ width: "24%" }} />}
          </colgroup>

          <thead>
            <tr style={{ textAlign: "left", fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>
              <th style={thTdPad}></th>
              <th style={hdrNum}>{budgetHeader}</th>
              <th style={hdrNum}>Actual</th>
              {!isMobile && <th style={thTdPad}>Variance</th>}
            </tr>
          </thead>

          <tbody>
            {/* Income */}
            <tr>
              <td style={{ ...thTdPad, fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Income</td>
              <td style={{ ...numCell, color: TRELLI_GREEN, fontWeight: 700 }}>{toUSD(bva.bInc)}</td>
              <td style={{ ...numCell, color: TRELLI_GREEN, fontWeight: 700 }}>{toUSD(bva.aInc)}</td>
              {!isMobile && <td style={{ ...thTdPad, color: "#111", fontWeight: 700 }}>{toUSD(bva.aInc - bva.bInc)}</td>}
            </tr>

            {/* Expenses */}
            <tr>
              <td style={{ ...thTdPad, fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Expenses</td>
              <td style={{ ...numCell, color: TRELLI_RED, fontWeight: 700 }}>{toUSD(bva.bExp)}</td>
              <td style={{ ...numCell, color: TRELLI_RED, fontWeight: 700 }}>{toUSD(bva.aExp)}</td>
              {!isMobile && <td style={{ ...thTdPad, color: "#111", fontWeight: 700 }}>{toUSD(bva.aExp - bva.bExp)}</td>}
            </tr>

            {/* Savings/Goals */}
            <tr>
              <td style={{ ...thTdPad, fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Savings/Goals</td>
              <td style={{ ...numCell, color: TRELLI_GOLD, fontWeight: 700 }}>{toUSD(bva.bSav || 0)}</td>
              <td style={{ ...numCell, color: TRELLI_GOLD, fontWeight: 700 }}>{toUSD(bva.aSav || 0)}</td>
              {!isMobile && (
                <td style={{ ...thTdPad, color: "#111", fontWeight: 700 }}>
                  {toUSD((bva.aSav || 0) - (bva.bSav || 0))}
                </td>
              )}
            </tr>

            {/* Net */}
            <tr>
              <td style={{ ...thTdPad, fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Net</td>
              <td style={{ ...numCell, color: "#111", fontWeight: 700 }}>
                {toUSD(bva.bInc - (bva.bExp + (bva.bSav || 0)))}
              </td>
              <td style={{ ...numCell }}>
                <span style={{ color: "#111", fontWeight: 700 }}>{toUSD(bva.aNet)}</span>
              </td>
              {!isMobile && (
                <td style={{ ...thTdPad, color: "#111", fontWeight: 700 }}>
                  {toUSD(bva.aNet - (bva.bInc - (bva.bExp + (bva.bSav || 0))))}
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {loading && <div style={{ marginTop: 8, fontSize: 12, color: "#999" }}>Loading…</div>}
    </>
  );

  if (noChrome) {
    // No outer card, no header — just the table block (used by mobile toggle)
    return tableBlock;
  }

  return (
    <div className="dash-card" style={{ ...cardStyle, maxWidth: "100%", overflowX: "hidden" }}>
      {!noHeader && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <div style={{ fontSize: 14, color: "#666" }}>{title}</div>
          <TimeBadge basis={badge} />
        </div>
      )}
      {tableBlock}
    </div>
  );
};

const BvaMTDCard = (props) => <BvaCard title="Budget vs Actual" badge="current" {...props} />;
const BvaL3Card  = (props) => <BvaCard title="Budget vs Actual" badge="last3"   {...props} />;

// ── Budget Beats / Misses / Movers (helpers for mobile + reuse on desktop) ──
//                   ▼ add "stretch = false"
const ListCard = ({ title, badge, rows = [], color = "#111", isMobile, stretch = false }) => {
  const outerStyle =
    color === TRELLI_GREEN ? cardGreenStyle :
    color === TRELLI_RED   ? cardRedStyle   :
    cardStyle;

  return (
    <div
      className="dash-card"
      style={{
        ...outerStyle,
        maxWidth: "100%",
        overflowX: "hidden",
        // ▼ only stretch when explicitly requested (desktop equal-height rows)
        ...(stretch ? { height: "100%", display: "flex", flexDirection: "column" } : {})
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 14, color: "#666" }}>{title}</div>
        {badge && <TimeBadge basis={badge} />}
      </div>

      {/* ▼ let the grid take the remaining space if stretching */}
      <div style={{ marginTop: 10, ...(stretch ? { flex: 1 } : null) }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0,1fr) 120px",
            columnGap: 12,
            rowGap: 8,
            alignItems: "baseline",
          }}
        >
          {/* header */}
          <div
            style={{
              fontSize: 12,
              color: "#555",
              textTransform: "uppercase",
              fontWeight: 700,
              letterSpacing: "0.02em",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            }}
          >
            Category / Subcategory
          </div>
          <div
            style={{
              fontSize: 12,
              color: "#555",
              textTransform: "uppercase",
              fontWeight: 700,
              letterSpacing: "0.02em",
              textAlign: "right",
              paddingRight: isMobile ? 4 : 0,
              fontVariantNumeric: "tabular-nums"
            }}
          >
            Amount
          </div>

          {rows.map((r, i) => (
            <React.Fragment key={i}>
              {/* label — clamp & ellipsis to prevent overlap */}
              <div
                title={r.label}
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  minWidth: 0,
                  fontSize: 14,
                }}
              >
                {r.label?.length > 36 && isMobile ? r.label.slice(0, 35) + "…" : r.label}
              </div>

              {/* amount — right aligned, same numeric feel as BvA */}
              <div
                style={{
                  textAlign: "right",
                  fontWeight: 700,
                  fontSize: 14,
                  color,
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {toUSD(r.amount || r.value || 0)}
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

// Current Month
const BeatsCard = ({ beats, isMobile, ...rest }) => (
  <ListCard
    title="Budget Beats"
    badge="current"
    isMobile={isMobile}
    color={TRELLI_GREEN}
    rows={[
      ...(beats?.beatCatsTop || []),
      ...(beats?.beatSubsTop || []),
    ]}
    {...rest}
  />
);

const MissesCard = ({ beats, isMobile, ...rest }) => (
  <ListCard
    title="Budget Misses"
    badge="current"
    isMobile={isMobile}
    color={TRELLI_RED}
    rows={[
      ...(beats?.missCatsTop || []),
      ...(beats?.missSubsTop || []),
    ]}
    {...rest}
  />
);

const MoversUpCard = ({ movers, isMobile, ...rest }) => (
  <ListCard
    title="Top Movers — Favorably"
    badge="last3"
    isMobile={isMobile}
    color={TRELLI_GREEN}
    rows={[
      ...(movers?.catsImprovingTop || []).map(r => ({ label: r.label, amount: Math.abs(r.slope) })),
      ...(movers?.subsImprovingTop || []).map(r => ({ label: r.label, amount: Math.abs(r.slope) })),
    ]}
    {...rest}
  />
);

const MoversDownCard = ({ movers, isMobile, ...rest }) => (
  <ListCard
    title="Top Movers — Unfavorably"
    badge="last3"
    isMobile={isMobile}
    color={TRELLI_RED}
    rows={[
      ...(movers?.catsWorseningTop || []).map(r => ({ label: r.label, amount: Math.abs(r.slope) })),
      ...(movers?.subsWorseningTop || []).map(r => ({ label: r.label, amount: Math.abs(r.slope) })),
    ]}
    {...rest}
  />
);

const PieLegendList = ({ slices = [], colorFor, isMobile }) => {
  // ensure at most 5 items (already top5+Other upstream; this keeps us safe)
  const rows = (slices || []).slice(0, 6);

  return (
    <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "minmax(0,1fr) 120px", columnGap: 12, rowGap: 8 }}>
      {/* headers */}
      <div style={{ fontSize: 12, color: "#555", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.02em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        Category / Subcategory
      </div>
      <div style={{ fontSize: 12, color: "#555", textTransform: "uppercase", fontWeight: 700, letterSpacing: "0.02em", textAlign: "right", paddingRight: isMobile ? 4 : 0, fontVariantNumeric: "tabular-nums" }}>
        Amount
      </div>

      {rows.map((s, i) => (
        <React.Fragment key={i}>
          <div title={s.label} style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, fontSize: 14 }}>
            {/* tiny color swatch */}
            <span style={{
              display: "inline-block", width: 10, height: 10, borderRadius: 2,
              background: (colorFor ? colorFor(s.label) : "#999"), marginRight: 6, verticalAlign: "middle"
            }} />
            {isMobile && s.label?.length > 36 ? s.label.slice(0, 35) + "…" : (s.label || "(Other)")}
          </div>
          <div style={{ textAlign: "right", fontWeight: 700, fontSize: 14, fontVariantNumeric: "tabular-nums" }}>
            {toUSD(s.value || 0)}
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

const PieMobileCard = ({ incSlices, expSlices, savSlices, isMobile }) => {
  // default to "expense" tab on mobile
  const [tab, setTab] = useState("expense"); // "income" | "expense" | "savings"

  const current =
    tab === "income" ? incSlices : tab === "savings" ? savSlices : expSlices;

  const colorFor =
    tab === "income"  ? incomeColorFor :
    tab === "savings" ? savingsColorFor :
                        expenseColorFor;

  return (
    <div className="dash-card" style={{ ...cardStyle, maxWidth: "100%", overflowX: "hidden" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 14, color: "#666" }}>Breakdown by Category</div>
        <TimeBadge basis="current" />
      </div>

      {/* Pills row — centered on mobile */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 8,
          margin: "8px 0",
          flexWrap: "wrap"
        }}
      >
        {[
          { key: "income",  label: "Income",         color: TRELLI_GREEN, bg: "#F2FBF7" },
          { key: "expense", label: "Expense",        color: TRELLI_RED,   bg: "#FFF5F5" },
          { key: "savings", label: "Savings/Goals",  color: TRELLI_GOLD,  bg: "#FFF7DA" }
        ].map(p => {
          const selected = tab === p.key;
          return (
            <button
              key={p.key}
              onClick={() => setTab(p.key)}
              style={{
                borderRadius: 999,
                padding: "6px 12px",
                border: `1px solid ${selected ? p.color : "#ddd"}`,
                background: selected ? p.bg : "#fff",
                color: p.color,            // colored text per pill
                fontWeight: 700,
                lineHeight: 1,
                cursor: "pointer",
                minWidth: 0
              }}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Pie — IMPORTANT: hide inline SVG legend on mobile */}
      <div className="trelli-no-vw" style={{ marginTop: 6 }}>
        <MiniPieChart
          data={tab === "income" ? incSlices : tab === "expense" ? expSlices : savSlices}
          mode={tab}
          showLegend={false}
          cxShift={0}
        />
      </div>

      {/* Single legend list below the pie */}
      <PieLegendList slices={current} colorFor={colorFor} isMobile />
    </div>
  );
};

const NetByMonthMobileCard = ({ data }) => (
  <div className="dash-card" style={{ ...cardStyle, maxWidth: "100%", overflowX: "hidden" }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
      <div style={{ fontSize: 14, color: "#666" }}>Net by Month</div>
      <TimeBadge basis="last3" />
    </div>

    <div
      style={{
        display: "flex",
        gap: 12,
        flexWrap: "wrap",
        alignItems: "center",
        marginTop: 8,
        fontSize: 12
      }}
    >
      <LegendLine color={TRELLI_GREEN} label="Net" />
      <LegendLine color={TRELLI_GOLD}  label="Average" dash="6 4" />
      <LegendLine color="#111"         label="Trend"   dash="6 4" strokeWidth={3} />
    </div>

    <div className="trelli-no-vw" style={{ marginTop: 10 }}>
      <MiniLineChart
        data={data}
        height={180}
        padding={24}
        gap={32}
        labelStep={2}
      />
    </div>
  </div>
);

const NetByMonthDesktopCard = ({ data }) => (
  <div
    className="dash-card"
    style={{
      ...cardStyle,
      maxWidth: "100%",
      overflowX: "hidden",
      gridColumn: "1 / -1"   // ⬅️ span both grid columns on desktop
    }}
  >
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
      <div style={{ fontSize: 14, color: "#666" }}>Net by Month</div>
      <TimeBadge basis="last3" />
    </div>

    <div className="trelli-no-vw" style={{ marginTop: 10 }}>
      <MiniLineChart
        data={data}
        height={240}
        padding={28}
        gap={90}
        labelStep={1}
        fixedWidth={1200}
      />
    </div>
  </div>
);

// --- Income vs Expense (BAR) — MOBILE card with title + badge + legend row ---
const IEBarMobileCard = ({ data }) => {
  // Short title so it fits in one line on phones
  const title = "Income vs Expense — Bar";

  return (
    <div className="dash-card" style={{ ...cardStyle, maxWidth: "100%", overflowX: "hidden" }}>
      {/* Title + badge on the top line */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 14, color: "#666", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {title}
        </div>
        <TimeBadge basis="last3" />
      </div>

      {/* Legend row on its own line (so it never gets clipped) */}
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          alignItems: "center",
          marginTop: 8,
          fontSize: 12
        }}
      >
        <LegendSwatch color={TRELLI_GREEN} label="Income" />
        <LegendSwatch color={TRELLI_RED}   label="Expense" />
        <LegendSwatch color={TRELLI_GOLD}  label="Savings/Goals" />
        <LegendSwatch color="#111"         label="Net" />
      </div>

      {/* Chart */}
      <div className="trelli-no-vw" style={{ marginTop: 8 }}>
        <MiniBarChart data={data} height={260} padding={24} />
      </div>
    </div>
  );
};

// --- Month-End Projections — MOBILE card (Current Month only) ---
const ProjectionsMobileCard = ({
  projected = { inc:0, exp:0, sav:0, net:0 },
  loading,
  showInfo = false,
  onToggleInfo = () => {}
}) => (
  <div className="dash-card" style={{ ...cardStyle, maxWidth: "100%", overflowX: "hidden" }}>
    {/* Header with title, alert icon, and Current badge */}
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ fontSize: 14, color: "#666" }}>Month-End Projections</div>
        {/* tap for info (mobile only; we are already in the mobile card) */}
        <button
          aria-label="Projection details"
          onClick={onToggleInfo}
          style={{
            width: 22, height: 22, borderRadius: 999, border: `1px solid ${TRELLI_GOLD}`,
            background: "#FFFAE8", color: "#7A5A00", display: "grid", placeItems: "center",
            cursor: "pointer", padding: 0, lineHeight: 1
          }}
          title="About projections"
        >
          <span style={{ fontSize: 13, fontWeight: 700 }}>i</span>
        </button>
      </div>
      <TimeBadge basis="current" />
    </div>

    {/* mirror Net Summary grid + type colors */}
    <div style={{ marginTop: 10, display: "grid", gridTemplateColumns: "1fr", gap: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Income</div>
        <div style={{ fontWeight: 700, color: TRELLI_GREEN }}>{toUSD(projected.inc)}</div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Expenses</div>
        <div style={{ fontWeight: 700, color: TRELLI_RED }}>{toUSD(projected.exp)}</div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Savings/Goals</div>
        <div style={{ fontWeight: 700, color: TRELLI_GOLD }}>{toUSD(projected.sav)}</div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Net</div>
        <div style={{ fontWeight: 700, color: "#111" }}>{toUSD(projected.net)}</div>
      </div>
    </div>

    {/* Mobile info popover */}
    {showInfo && (
      <div
        style={{
          marginTop: 10,
          display: "flex",
          alignItems: "flex-start",
          gap: 8,
          background: "#FFFAE8",
          border: `1px solid ${TRELLI_GOLD}`,
          padding: "8px 10px",
          borderRadius: 8,
          color: "#7A5A00",
          fontSize: 12
        }}
      >
        <AlertIcon title="Projection notice" />
        <div>
          These numbers are projections based on your budgets and recent activity. 
          If some transactions need action, projections may shift as you confirm or edit them.
        </div>
      </div>
    )}

    {loading && <div style={{ marginTop: 8, fontSize: 12, color: "#999" }}>Loading…</div>}
  </div>
);

// tiny legend helper used above
const LegendSwatch = ({ color, label }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    <span style={{ width: 10, height: 10, borderRadius: 2, background: color }} />
    <span style={{ color: "#333" }}>{label}</span>
  </span>
);

const LegendLine = ({ color, label, dash, strokeWidth = 2 }) => (
  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
    <svg width="18" height="10" aria-hidden="true">
      <line
        x1="0" y1="6" x2="18" y2="6"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeDasharray={dash || ""}
      />
    </svg>
    <span style={{ color: "#333" }}>{label}</span>
  </span>
);

// mm/dd/yy for mobile — keeps it narrow; tolerant of Timestamp, ISO, Date, etc.
const formatDateShort = (d) => {
  try {
    const dt =
      d && typeof d.toDate === "function" ? d.toDate()
      : d instanceof Date ? d
      : d ? new Date(d)
      : null;
    if (!dt || isNaN(dt.getTime())) return "—";
    // Safari-safe: rely on locale with 2-digit year; month/day can be 1-2 digits
    return dt.toLocaleDateString("en-US", { year: "2-digit", month: "numeric", day: "numeric" });
  } catch {
    return "—";
  }
};

// --- Error boundary to catch render crashes (especially on mobile) ---
class ErrorCatcher extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: null, info: null };
  }
  componentDidCatch(err, info) {
    console.error("[Dashboard ErrorBoundary]", err, info);
    this.setState({ err, info });
  }
  render() {
    if (this.state.err) {
      return (
        <div style={{
          padding: "12px",
          background: "#FFF5F5",
          border: "1px solid #F5D4D4",
          borderRadius: 8,
          color: "#7A1F1F",
          fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
          whiteSpace: "pre-wrap"
        }}>
          <div style={{ fontWeight: 800, marginBottom: 6 }}>Something broke rendering the Dashboard.</div>
          <div style={{ fontSize: 13 }}>
            {(this.state.err?.message || String(this.state.err))}
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// month key helper and simple linear regression slope (like Reports)
const ymKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
const asDate = (d) => (d instanceof Date ? d : new Date(d));
// --- Reports-consistent helpers ---
const isBudgetRow = (r) => r?._type && r._type !== "non-budget";
const isIncome = (r) => r?._type === "income";
const isOutflow = (r) => r?._type === "expense" || r?._type === "savings";
const isSavings = (r) => r?._type === "savings";
// Signed amount used by Reports net math (income positive, expenses/savings negative, refunds positive)
const signedForNet = (r) => Number(r?.amount || 0);
// Outflow magnitude used for “Expenses” buckets (refunds reduce)
function addToExpenseMagnitude(cur, amt) {
  const a = Number(amt || 0);
  if (a < 0) return cur + Math.abs(a); // spending
  if (a > 0) return Math.max(0, cur - a); // refund reduces spend
  return cur;
}
// Normalize label keys
const catLabelOf = (r) => r?.category || "(Uncategorized)";
const subLabelOf = (r) => r?.subcategory ? `${r.category}: ${r.subcategory}` : `${r.category}: (Uncategorized)`;
const MON = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const mon3 = (ym) => { const m = Math.max(1, Math.min(12, Number(ym.slice(5,7)) || 0)) - 1; return MON[m] || ym; };
function trendFor(values) {
  const n = values.length;
  if (n < 2) return 0;
  let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
  for (let i = 0; i < n; i++) {
    const x = i, y = values[i] ?? 0;
    sumX += x; sumY += y; sumXY += x * y; sumXX += x * x;
  }
  const denom = n * sumXX - sumX * sumX;
  return denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
}

// layout & card styles to keep things aligned and spacious
const gridStyle = {
  display: "grid",
  // wider minimum per column so cards stay roomy
  gridTemplateColumns: "repeat(2, minmax(460px, 1fr))",
  columnGap: "1rem",      // slightly tighter gap between columns
  rowGap: "1.25rem",
  marginBottom: "2rem",
  alignItems: "start",
};

// keep the original for desktop
const gridStyleMobile = {
  display: "block",       // collapse to one column
  rowGap: "1.25rem",      // keep your spacing
};

const colStyle = {
  display: "grid",
  gridAutoRows: "min-content",
  gap: "1.25rem",
};

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e5e5",
  borderRadius: 12,
  padding: "1.25rem", // slightly more padding
  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
  overflow: "hidden", // prevents content spilling outside
  boxSizing: "border-box"
};

const cardGreenStyle = {
  ...cardStyle,
  background: "#F2FBF7",        // very light Trelli green wash
  borderColor: "#CDEFE2",       // subtle green border
};

const cardRedStyle = {
  ...cardStyle,
  background: "#FFF5F5",        // very light red wash
  borderColor: "#F5D4D4",       // subtle red border
};

const tableStyle = {
  width: "100%",
  borderCollapse: "collapse",
  tableLayout: "fixed", // lock column widths to avoid overflow
};

const thTdPad = { padding: "8px 0", verticalAlign: "top" };

// --- Mini charts (pure SVG, no libs) ---

function MiniLineChart({
  data,
  height = 240,
  padding = 28,
  gap = 90,
  labelStep = 1,
  // NEW: when set, forces a specific SVG width (restores the old desktop feel)
  fixedWidth = null,
}) {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null); // {i, x, y, net, avg, trend}

  const isPhone = typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

  const vals = data.map(d => Number(d.net || 0));
  const labels = data.map(d => d.label);
  const n = vals.length;
  const empty = !Array.isArray(data) || n === 0;

  // NEW: compute a compact width based on the number of points and desired gap,
  // instead of a fixed 960px canvas. This is the key to compressing months.
  const width = fixedWidth ?? Math.max(
    320,
    padding * 2 + Math.max(0, n - 1) * Math.max(12, gap)
  );

  const min = Math.min(0, ...vals);
  const max = Math.max(0, ...vals, 1);

  const x = (i) => (n <= 1 ? padding : padding + (i * (width - padding * 2)) / (n - 1));
  const y = (v) => {
    const span = max - min || 1;
    return height - padding - ((v - min) * (height - padding * 2)) / span;
  };

  const zeroY = y(0);
  const avg = n ? vals.reduce((s, v) => s + v, 0) / n : 0;
  const avgY = y(avg);

  // trend
  let slope = 0, intercept = 0;
  if (n >= 2) {
    let sumX = 0, sumY = 0, sumXY = 0, sumXX = 0;
    for (let i = 0; i < n; i++) { const xi = i, yi = vals[i]; sumX += xi; sumY += yi; sumXY += xi*yi; sumXX += xi*xi; }
    const denom = n * sumXX - sumX * sumX;
    slope = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
    intercept = n ? (sumY - slope * sumX) / n : 0;
  }
  const trendAt = (i) => intercept + slope * i;

  const netPath = vals.map((v, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(v)}`).join(" ");

  function onMove(e) {
    if (empty) return;
    const svg = svgRef.current;
    if (!svg) return;

    // Map mouse to true SVG coords
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const ctm = svg.getScreenCTM(); if (!ctm) return;
    const sp = pt.matrixTransform(ctm.inverse()); // sp.x, sp.y in SVG units

    // Find nearest point by X in SVG units
    let nearest = 0, best = Infinity;
    for (let i = 0; i < n; i++) {
      const dx = Math.abs(sp.x - x(i));
      if (dx < best) { best = dx; nearest = i; }
    }
    const xi = x(nearest), yi = y(vals[nearest]);
    setHover({
      i: nearest,
      x: xi,
      y: yi,
      net: vals[nearest],
      avg,
      trend: trendAt(nearest),
    });
  }
  function onLeave() { setHover(null); }

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${width} ${height}`}
      style={{ width: "100%", height }}    // scales to card width, no horizontal scroll
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      {empty ? (
        <text x={width/2} y={height/2} textAnchor="middle" fontSize="12" fill="#999">No data for this period</text>
      ) : (
        <>
          {/* $0 baseline */}
          <line x1={padding} y1={zeroY} x2={width - padding} y2={zeroY}
                stroke="#d4d4d4" strokeDasharray="4 4" />
          <text x={padding} y={zeroY - 6} fontSize={isPhone ? "12" : "10"} fill="#777">$0</text>

          {/* avg (gold dashed) */}
          <line x1={padding} y1={avgY} x2={width - padding} y2={avgY}
                stroke="#F4C542" strokeDasharray="6 4" />

          {/* trend (thicker black dashed) */}
          <line x1={x(0)} y1={y(trendAt(0))} x2={x(n - 1)} y2={y(trendAt(n - 1))}
                stroke="#111" strokeDasharray="6 4" strokeWidth="3" />

          {/* net line (green) */}
          <path d={netPath} fill="none" stroke={TRELLI_GREEN} strokeWidth="2" />
          {vals.map((v, i) => (
            <circle key={i} cx={x(i)} cy={y(v)} r={3} fill={TRELLI_GREEN} />
          ))}

          {/* x labels — draw every `labelStep` to keep things tight on phones */}
          {labels.map((l, i) => (
            i % Math.max(1, labelStep) === 0 ? (
              <text key={i} x={x(i)} y={height - 6} textAnchor="middle" fontSize={isPhone ? "12" : "10"} fill="#777">
                {mon3(l)}
              </text>
            ) : null
          ))}

          {/* tooltip */}
          {hover && (
            <>
              <circle cx={hover.x} cy={hover.y} r={4} fill="#111" />
              <g transform={`translate(${Math.min(width - (isPhone ? 260 : 220), Math.max(hover.x + 8, padding))}, ${Math.max(padding, hover.y - (isPhone ? 90 : 56))})`}>
                {/* ↑ use 90 on phones so we have room for 3 lines */}

                <rect width={isPhone ? 250 : 210} height={isPhone ? 90 : 56} rx="6" fill="#fff" stroke="#e6e6e6" />

                {/* title */}
                <text x="8" y={isPhone ? 18 : 14} fontSize={isPhone ? "13" : "11"} fill="#111" style={{ fontWeight: 600 }}>
                  {mon3(labels[hover.i])}
                </text>

                {/* Net */}
                <text x="8" y={isPhone ? 36 : 28} fontSize={isPhone ? "13" : "11"} fill={TRELLI_GREEN}>
                  Net: {toUSD(hover.net)}
                </text>

                {/* Average */}
                <text x="8" y={isPhone ? 54 : 42} fontSize={isPhone ? "13" : "11"} fill="#F4C542">
                  Average: {toUSD(hover.avg)}
                </text>

                {/* Trend — on its own line on phones; same line as before on desktop */}
                <text x="8" y={isPhone ? 72 : 58} fontSize={isPhone ? "13" : "11"} fill="#111">
                  Trend: {toUSD(hover.trend)}
                </text>
              </g>
            </>
          )}
        </>
      )}
    </svg>
  );
}

function MiniBarChart({ data, height = 260, padding = 28, fixedWidth = null }) {
  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);

  // ✅ Mobile-aware sizing (mirrors the approach used in MiniLineChart)
  const isPhone = typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;

  // Give each month enough breathing room on phones so text is legible
  const gapPerMonth = isPhone ? 64 : 90;
  const minCanvas   = isPhone ? 360 : 720;

  // Dynamic SVG width so labels aren’t crammed (prevents title/legend pressure too)
  const width = fixedWidth ?? Math.max(
    minCanvas,
    padding * 2 + Math.max(1, (data?.length || 1)) * gapPerMonth
  );

  const empty = !Array.isArray(data) || data.length === 0;

  const maxAbs = Math.max(
    1,
    ...data.map(d => Math.max(
      Math.abs(d?.totals?.income || 0),
      Math.abs(d?.totals?.expense || 0),
      Math.abs(d?.totals?.savings || 0),
      Math.abs(d?.totals?.net || 0),
    ))
  );

  const xBand = (width - padding * 2) / (data.length || 1);
  const barW = Math.min(45, Math.max(16, (xBand - 6) / 4)); // 4 bars: Inc | Exp | Sav | Net
  const y = (v) => {
    const usable = height - padding * 2;
    const mid = padding + usable / 2;
    return mid - (v / (maxAbs * 1.1)) * (usable / 2); // 10% headroom
  };
  const zeroY = y(0);

  // Precompute rects in SVG coords for reliable hit-testing
  const months = data.map((d, i) => {
    const xCenter = padding + i * xBand + xBand / 2;
    const xInc = xCenter - (1.5 * barW);
    const xExp = xCenter - (0.5 * barW);
    const xSav = xCenter + (0.5 * barW);
    const xNet = xCenter + (1.5 * barW);

    // stacks
    let accUp = 0;
    const incRects = (d.incomeSegments || []).map(seg => {
      const v = Number(seg.value || 0);
      const y0 = y(accUp);
      const y1 = y(accUp + v);
      accUp += v;
      return { type: "inc", monthIndex: i, monthLabel: d.label, label: seg.label, value: v,
               total: d?.totals?.income || 0, x: xInc, y: Math.min(y0, y1), w: barW, h: Math.abs(y1 - y0),
               fill: incomeColorFor(seg.label) };
    });

    let accDownE = 0;
    const expRects = (d.expenseSegments || []).map(seg => {
      const v = Number(seg.value || 0);
      const y0 = y(-accDownE);
      const y1 = y(-(accDownE + v));
      accDownE += v;
      return { type: "exp", monthIndex: i, monthLabel: d.label, label: seg.label, value: v,
               total: d?.totals?.expense || 0, x: xExp, y: Math.min(y0, y1), w: barW, h: Math.abs(y1 - y0),
               fill: expenseColorFor(seg.label) };
    });

    let accDownS = 0;
    const savRects = (d.savingsSegments || []).map(seg => {
      const v = Number(seg.value || 0);
      const y0 = y(-accDownS);
      const y1 = y(-(accDownS + v));
      accDownS += v;
      return { type: "sav", monthIndex: i, monthLabel: d.label, label: seg.label, value: v,
               total: d?.totals?.savings || 0, x: xSav, y: Math.min(y0, y1), w: barW, h: Math.abs(y1 - y0),
               fill: savingsColorFor(seg.label) };
    });

    const netY2 = y(d?.totals?.net || 0);
    const netRect = {
      type: "net", monthIndex: i, monthLabel: d.label, label: "Net", value: d?.totals?.net || 0,
      total: d?.totals?.net || 0, x: xNet, y: Math.min(zeroY, netY2), w: barW, h: Math.abs(netY2 - zeroY),
      fill: "#111",
    };

    // always-on hover zones (cover entire bar slots; ensure they come AFTER seg rects in hit order)
    const incZone = { type: "inc", monthIndex: i, monthLabel: d.label, label: "Income",  value: 0,
      total: d?.totals?.income || 0, x: xInc, y: padding, w: barW, h: height - padding * 2, fill: "transparent" };
    const expZone = { type: "exp", monthIndex: i, monthLabel: d.label, label: "Expenses", value: 0,
      total: d?.totals?.expense || 0, x: xExp, y: padding, w: barW, h: height - padding * 2, fill: "transparent" };
    const savZone = { type: "sav", monthIndex: i, monthLabel: d.label, label: "Savings/Goals", value: 0,
      total: d?.totals?.savings || 0, x: xSav, y: padding, w: barW, h: height - padding * 2, fill: "transparent" };
    const netZone = { type: "net", monthIndex: i, monthLabel: d.label, label: "Net", value: d?.totals?.net || 0,
      total: d?.totals?.net || 0, x: xNet, y: padding, w: barW, h: height - padding * 2, fill: "transparent" };

    return { xCenter, label: d.label, incRects, expRects, savRects, netRect, incZone, expZone, savZone, netZone };
  });

  const hitRects = months.flatMap(m => [
    ...m.incRects, ...m.expRects, ...m.savRects, m.netRect,
    m.incZone, m.expZone, m.savZone, m.netZone
  ]);

  function onMove(e) {
    if (empty) return;
    const svg = svgRef.current; if (!svg) return;
    const pt = svg.createSVGPoint(); pt.x = e.clientX; pt.y = e.clientY;
    const ctm = svg.getScreenCTM(); if (!ctm) return;
    const sp = pt.matrixTransform(ctm.inverse());
    const T = 4;
    const hit = hitRects.find(r =>
      sp.x >= r.x - T && sp.x <= r.x + r.w + T &&
      sp.y >= r.y - T && sp.y <= r.y + r.h + T
    );
    if (!hit) return setHover(null);
    setHover({
      type: hit.type, month: mon3(hit.monthLabel), label: hit.label, value: hit.value,
      total: hit.total, x: hit.x + hit.w, y: hit.y,
    });
  }
  function onLeave() { setHover(null); }

  return (
    <svg ref={svgRef} viewBox={`0 0 ${width} ${height}`} style={{ width: "100%", height }} onMouseMove={onMove} onMouseLeave={onLeave}>
      {empty ? (
        <text x={width/2} y={height/2} textAnchor="middle" fontSize="12" fill="#999">No data for this period</text>
      ) : (
        <>
          {/* $0 baseline */}
          <line x1={padding} y1={zeroY} x2={width - padding} y2={zeroY} stroke="#d4d4d4" strokeDasharray="4 4" />
          <text x={padding} y={zeroY - 6} fontSize={isPhone ? "12" : "10"} fill="#777">$0</text>

          {months.map((m, i) => (
            <g key={i}>
              {m.incRects.map((r, k) => <rect key={`i-${k}`} x={r.x} y={r.y} width={r.w} height={r.h} fill={r.fill} rx="2" />)}
              {m.expRects.map((r, k) => <rect key={`e-${k}`} x={r.x} y={r.y} width={r.w} height={r.h} fill={r.fill} rx="2" />)}
              {m.savRects.map((r, k) => (
                <rect key={`s-${k}`} x={r.x} y={r.y} width={r.w} height={r.h} fill={r.fill} opacity="0.8" rx="2" />
              ))}

              {/* zero stubs at the $0 line when a bar’s total is exactly $0 */}
              {m.incRects.length === 0 && (m.incZone.total || 0) === 0 && (
                <line
                  x1={m.incZone.x}
                  x2={m.incZone.x + m.incZone.w}
                  y1={zeroY}
                  y2={zeroY}
                  stroke={TRELLI_GREEN}
                  strokeWidth="2"
                />
              )}
              {m.expRects.length === 0 && (m.expZone.total || 0) === 0 && (
                <line
                  x1={m.expZone.x}
                  x2={m.expZone.x + m.expZone.w}
                  y1={zeroY}
                  y2={zeroY}
                  stroke={TRELLI_RED}
                  strokeWidth="2"
                />
              )}
              {m.savRects.length === 0 && (m.savZone.total || 0) === 0 && (
                <line
                  x1={m.savZone.x}
                  x2={m.savZone.x + m.savZone.w}
                  y1={zeroY}
                  y2={zeroY}
                  stroke={TRELLI_GOLD}
                  strokeWidth="2"
                />
              )}
              {(m.netRect.h === 0 || (m.netZone.total || 0) === 0) && (
                <line
                  x1={m.netZone.x}
                  x2={m.netZone.x + m.netZone.w}
                  y1={zeroY}
                  y2={zeroY}
                  stroke="#111"
                  strokeWidth="2"
                />
              )}

              <rect x={m.netRect.x} y={m.netRect.y} width={m.netRect.w} height={m.netRect.h} fill={m.netRect.fill} rx="1" />
              <text x={m.xCenter} y={height - 6} textAnchor="middle" fontSize={isPhone ? "12" : "10"} fill="#777">
                {mon3(m.label)}
              </text>
            </g>
          ))}

          {/* tooltip */}
          {hover && (
            <g transform={`translate(${Math.min(width - (isPhone ? 260 : 220), Math.max(hover.x + 8, padding))}, ${Math.max(padding, hover.y - (isPhone ? 72 : 56))})`}>
              <rect width={isPhone ? 250 : 210} height={isPhone ? 68 : 56} rx="6" fill="#fff" stroke="#e6e6e6" />
              <text x="8" y={isPhone ? 18 : 14} fontSize={isPhone ? "13" : "11"} fill="#111" style={{ fontWeight: 600 }}>{hover.month}</text>
              {hover.type === 'inc' && (
                <>
                  <text x="8" y={isPhone ? 36 : 28} fontSize={isPhone ? "13" : "11"} fill={TRELLI_GREEN}>Income: {toUSD(hover.total)}</text>
                  <text x="8" y={isPhone ? 54 : 42} fontSize={isPhone ? "13" : "11"} fill="#555">{hover.label}: {toUSD(hover.value)}</text>
                </>
              )}
              {hover.type === 'exp' && (
                <>
                  <text x="8" y={isPhone ? 36 : 28} fontSize={isPhone ? "13" : "11"} fill={TRELLI_RED}>Expenses: {toUSD(hover.total)}</text>
                  <text x="8" y={isPhone ? 54 : 42} fontSize={isPhone ? "13" : "11"} fill="#555">{hover.label}: {toUSD(hover.value)}</text>
                </>
              )}
              {hover.type === 'sav' && (
                <>
                  <text x="8" y={isPhone ? 36 : 28} fontSize={isPhone ? "13" : "11"} fill={TRELLI_GOLD}>Savings/Goals: {toUSD(hover.total)}</text>
                  <text x="8" y={isPhone ? 54 : 42} fontSize={isPhone ? "13" : "11"} fill="#555">{hover.label}: {toUSD(hover.value)}</text>
                </>
              )}
              {hover.type === 'net' && (
                <text x="8" y={isPhone ? 44 : 34} fontSize={isPhone ? "13" : "11"} fill="#111">Net: {toUSD(hover.value)}</text>
              )}
            </g>
          )}
        </>
      )}
    </svg>
  );
}

function MiniPieChart({
  data,
  mode = "expense",
  height = 240,
  innerRadius = 44,
  showLegend = true,      // ← NEW: let callers hide the inline legend
  cxShift = 0,            // ← NEW: nudge donut horizontally (desktop spacing)
  legendValueX = 240,     // ← NEW: where the $ values align
  legendRowGap = 20,      // ← NEW: vertical spacing between legend rows
  legendLabelMax = 26     // ← NEW: clamp label length before adding “ … ”
}) {

  const svgRef = useRef(null);
  const [hover, setHover] = useState(null);
  const empty = !Array.isArray(data) || data.length === 0 || data.every(d => Number(d.value || 0) === 0);

  // mobile-aware tooltip sizing
  const isPhone = typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches;
  const tipFont = isPhone ? 18 : 15;   // larger on phones
  const tipW    = isPhone ? 240 : 180; // wider box on phones
  const tipH    = isPhone ? 54  : 40;  // taller box on phones
  const tipY1   = isPhone ? 20  : 16;  // first line baseline
  const tipY2   = isPhone ? 38  : 30;  // second line baseline

  // canvas + positions
  const width = 560;

  const cx = width / 2 + cxShift; 
  const cy = height / 2;
  const r  = Math.min(width, height) / 2 - 20;

  // legend positions (desktop inline only)
  const legendX       = 10;

  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0) || 1;
  let a0 = -Math.PI / 2; // start at top

  const colorFor =
    mode === "income"  ? incomeColorFor  :
    mode === "savings" ? savingsColorFor :
                         expenseColorFor;

  function arcPath(a0, a1) {
    const large = a1 - a0 > Math.PI ? 1 : 0;
    const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
    const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
    const xi = cx + innerRadius * Math.cos(a1), yi = cy + innerRadius * Math.sin(a1);
    const xo = cx + innerRadius * Math.cos(a0), yo = cy + innerRadius * Math.sin(a0);
    return `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} L ${xi} ${yi} A ${innerRadius} ${innerRadius} 0 ${large} 0 ${xo} ${yo} Z`;
  }

  // Build slices AND keep angles for hit-testing
  // a0 starts at -PI/2 and increases; we also keep a normalized progress [0..2PI)
  let progress = 0; // normalized angle from 0..2PI, where 0 corresponds to -PI/2
  const slices = empty ? [] : data.map(d => {
    const v = Math.max(0, Number(d.value || 0));
    const span = (v / total) * Math.PI * 2;

    const start = a0;           // real radians (-PI..PI] while accumulating
    const end   = a0 + span;
    const path  = arcPath(start, end);

    const startN = progress;    // normalized 0..2PI starting at top
    const endN   = progress + span;
    progress += span;
    a0 = end;

    return {
      label: d.label || "(Other)",
      value: v,
      fill: colorFor(d.label || ""),
      path,
      // angles for hit-testing
      start, end, startN, endN
    };
  });

  function onMove(e) {
    if (empty) return;
    const svg = svgRef.current; if (!svg) return;

    // Mouse → SVG coordinates
    const pt = svg.createSVGPoint();
    pt.x = e.clientX; pt.y = e.clientY;
    const ctm = svg.getScreenCTM(); if (!ctm) return;
    const sp = pt.matrixTransform(ctm.inverse()); // sp.x, sp.y in SVG units

    // Polar from pie center
    const dx = sp.x - cx;
    const dy = sp.y - cy;
    const rad = Math.hypot(dx, dy);

    // Outside the donut ring? bail
    if (rad < innerRadius || rad > r) {
      setHover(null);
      return;
    }

    // Angle from X-axis; we want 0..2PI with 0 at -PI/2 (12 o'clock)
    let ang = Math.atan2(dy, dx);          // (-PI, PI]
    // Normalize so that -PI/2 -> 0, and increase clockwise around
    let angN = ang - (-Math.PI / 2);
    if (angN < 0) angN += Math.PI * 2;     // 0..2PI

    // Find slice where startN <= angN < endN
    const hit = slices.find(s => angN >= s.startN && angN < s.endN);
    if (!hit) { setHover(null); return; }

    // Tooltip position (keep within viewBox)
    const tipX = Math.min(width - (tipW + 12), Math.max(sp.x + 8, 12));
    const tipY = Math.max(12, sp.y - 46);

    setHover({
      x: tipX,
      y: tipY,
      label: hit.label,
      value: hit.value,
      fill: hit.fill
    });
  }
  function onLeave() { setHover(null); }

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${width} ${height}`}
      style={{ width: "100%", height }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      {empty ? (
        <text x={width/2} y={height/2} textAnchor="middle" fontSize="12" fill="#999">
          No data for this period
        </text>
      ) : (
        <>
          {/* If there is only one slice, draw a full circle (SVG can't draw a 360° arc path) */}
          {slices.length === 1 ? (
            <>
              <circle cx={cx} cy={cy} r={r} fill={slices[0].fill} />
            </>
          ) : (
            slices.map((a, i) => <path key={i} d={a.path} fill={a.fill} stroke="#fff" />)
          )}

          {/* Donut hole */}
          <circle cx={cx} cy={cy} r={innerRadius - 1} fill="#fff" />

          {/* tooltip (mobile-aware sizing) */}
          {hover && (
            <g transform={`translate(${hover.x}, ${hover.y})`}>
              <rect width={tipW} height={tipH} rx="6" fill="#fff" stroke="#e6e6e6" />
              <text x="8" y={tipY1} fontSize={tipFont} fill={hover.fill} style={{ fontWeight: 700 }}>
                {hover.label}
              </text>
              <text x="8" y={tipY2} fontSize={tipFont} fill="#111">
                {toUSD(hover.value)}
              </text>
            </g>
          )}

          {/* legend (desktop inline) */}
          {showLegend && slices.slice(0, 8).map((a, i) => (
            <g key={i} transform={`translate(${10}, ${20 + i * legendRowGap})`}>
              <rect width="10" height="10" fill={a.fill} rx="2" />
              <text x="16" y="10" fontSize="11" fill={a.fill} style={{ fontWeight: 700 }}>
                {a.label ? (a.label.length > legendLabelMax ? a.label.slice(0, legendLabelMax - 1) + "…" : a.label) : "(Other)"}
              </text>
              <text x={legendValueX} y="10" fontSize="11" fill="#444" textAnchor="end">
                {toUSD(a.value)}
              </text>
            </g>
          ))}
        </>
      )}
    </svg>
  );
}

// Fullscreen blur + spinner, rendered via portal so placement doesn't matter
const BootOverlay = ({ show, message = "Your financial freedom is loading…" }) => {
  if (!show) return null;
  return createPortal(
    <>
      <style>{`
        @keyframes trelliSpin { 
          from { transform: rotate(0deg); } 
          to   { transform: rotate(360deg); } 
        }
      `}</style>
      <div
        role="status"
        aria-live="polite"
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 9999,
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(255,255,255,0.65)",
          display: "grid",
          placeItems: "center"
        }}
      >
        <div style={{ textAlign: "center", padding: 16 }}>
          {/* Spinner */}
          <div
            style={{
              width: 48, height: 48, borderRadius: "50%",
              border: "4px solid rgba(0,0,0,0.08)",
              borderTop: `4px solid ${TRELLI_GREEN}`,
              animation: "trelliSpin 1s linear infinite",
              margin: "0 auto"
            }}
          />
          {/* Message */}
          <div style={{ marginTop: 12, fontWeight: 800, color: "#111" }}>
            {message}
          </div>
          <div style={{ marginTop: 4, fontSize: 12, color: "#666" }}>
            Just a moment…
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default function Dashboard() {
  console.log("[Dashboard] render @", new Date().toISOString());
  // ADD ▶ render counter
  console.count("[Dashboard] renders");

  const navigate = useNavigate();
  const location = useLocation();

  const goToNeedsAction = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    navigate("/transactions?filter=confirm");
  };

  useLayoutEffect(() => {
    // Take over scroll position management
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    // First render → top
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // On route path change → top
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    // When returning from bfcache (mobile), ensure top
    const onShow = (e) => {
      if (e.persisted) window.scrollTo(0, 0);
    };
    window.addEventListener("pageshow", onShow);
    return () => window.removeEventListener("pageshow", onShow);
  }, []);

  const [accountsConnected, setAccountsConnected] = useState(false);
  const [linkToken, setLinkToken] = useState(null);

  const [plaidBroken, setPlaidBroken] = useState(false);
  const [plaidErrCode, setPlaidErrCode] = useState(null);

  const [booting, setBooting] = useState(true);

  // auth + data
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cmTotals, setCmTotals] = useState({ income: 0, expenses: 0, net: 0 }); // current month
  const [lm3Totals, setLm3Totals] = useState({ income: 0, expenses: 0, net: 0 }); // last 3 full months
  const [bvaCm, setBvaCm] = useState({ bInc: 0, bExp: 0, bNet: 0, aInc: 0, aExp: 0, aNet: 0, vNet: 0 });
  const [bvaL3, setBvaL3] = useState({ bInc: 0, bExp: 0, bNet: 0, aInc: 0, aExp: 0, aNet: 0, vNet: 0 });
  const [beats, setBeats] = useState({
    beatCatsTop: [], missCatsTop: [], beatSubsTop: [], missSubsTop: []
  });
  const [movers, setMovers] = useState({
    catsImprovingTop: [], catsWorseningTop: [], subsImprovingTop: [], subsWorseningTop: []
  });
  // rows for charting
  const [cmRowsState, setCmRowsState] = useState([]);
  // Includes unconfirmed/unmatched so Needs Action can see them
  const [cmRowsAllState, setCmRowsAllState] = useState([]);
  // Wider unscoped feed for Needs Action (e.g., last 180 days, confirmed + unconfirmed)
  const [needsRowsState, setNeedsRowsState] = useState([]);
  const [l3RowsState, setL3RowsState] = useState([]);
  // chart series
  const [seriesL3Net, setSeriesL3Net] = useState([]);      // [{label, net}]
  const [seriesL3IE, setSeriesL3IE] = useState([]);        // [{label, income, expense}]
  const [pieIncomeCM, setPieIncomeCM] = useState([]);
  const [pieExpenseCM, setPieExpenseCM] = useState([]);
  const [pieSavingsCM, setPieSavingsCM] = useState([]);                // [{label, value}]

  // Full, unscoped tx feed (IDENTICAL dataset used by BudgetBuilderManual Needs Action)
  const [allTxState, setAllTxState] = useState([]);

  // Map Plaid accountId -> friendly display name (e.g., "Chase Sapphire • 1234")
  const [accountNameById, setAccountNameById] = useState({});

  useEffect(() => {
    if (!user?.uid) {
      setAccountNameById({});
      return;
    }

    const ref = collection(db, "users", user.uid, "plaid", "accounts", "byId");

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const next = {};
        snap.forEach((docSnap) => {
          const d = docSnap.data() || {};
          const name =
            d.name ||
            d.official_name ||
            d.officialName ||
            "";
          const mask = d.mask ? String(d.mask) : "";
          const label =
            name && mask ? `${name} • ${mask}` :
            name ? name :
            mask ? mask :
            docSnap.id;

          next[docSnap.id] = label;
        });
        setAccountNameById(next);
      },
      (err) => {
        console.error("[Dashboard] accounts/byId subscription failed:", err);
        setAccountNameById({});
      }
    );

    return () => unsub();
  }, [user?.uid]);

  // Needs Action + Projected Month End Net (badges)
  const [budgetDocsState, setBudgetDocsState] = useState([]);

  // 🔍 DEBUG: simple client-side tracer (no-op unless used)
  const __dbg = {
    wrapTxWrite(label, fn) {
      return async (...args) => {
        const t0 = performance.now();
        try {
          const res = await fn(...args);
          console.log(`[TX-WRITE:${label}] ok in ${(performance.now()-t0).toFixed(1)}ms`, { args });
          return res;
        } catch (e) {
          console.error(`[TX-WRITE:${label}] ERROR`, e, { args });
          throw e;
        }
      };
    }
  };

  // ADD ▶ re-render cause tracer
  const __lastRef = useRef({});
  useEffect(() => {
    const now = {
      allTxLen: (allTxState || []).length,
      needsLen: (uncategorizedTransactions || []).length,
      budgetsLen: (dropdownBudgets || []).length,
      cmRows: (cmRowsState || []).length,
      l3Rows: (l3RowsState || []).length,
      suggestionsReady,
    };
    const prev = __lastRef.current || {};
    const diffs = Object.entries(now)
      .filter(([k, v]) => v !== prev[k])
      .map(([k, v]) => `${k}: ${prev[k]} → ${v}`);
    if (diffs.length) {
      console.log("[Dashboard] state change →", diffs.join(", "));
      __lastRef.current = now;
    }
  });

  useEffect(() => {
    const m = window.matchMedia("(max-width: 768px)").matches;
    console.log("[MobileDetect]", {
      innerWidth: window.innerWidth,
      devicePixelRatio: window.devicePixelRatio,
      matchMedia768: m,
    });
  }, []);

  // 🔍 DEBUG: expose a watcher for "needs-review" transactions in the last 180 days
  useEffect(() => {
    if (!user) return;
    window.__txWatchNeeds = (days = 180, cap = 1000) => {
      const since = new Date(Date.now() - days * 86400000);
      // Query ONLY by userId + cap; filter by date client-side to avoid index
      const qRef = query(
        collection(db, "transactions"),
        where("userId", "==", user.uid),
        fsLimit(cap)
      );
      console.group("[__txWatchNeeds] attach");
      console.log("uid:", user.uid, "since:", since.toISOString(), "cap:", cap);
      console.groupEnd();
      return onSnapshot(qRef, (snap) => {
        snap.docChanges().forEach((chg) => {
          const d = chg.doc.data();
          const ts = d?.date?.toDate ? d.date.toDate() : d?.date;
          if (ts && ts < since) return;

          // Only print rows relevant to this bug
          if (d?.needsReview === true || d?.status === "needs-review" || d?.status === "confirmed") {
            console.group(`[__txWatchNeeds] ${chg.type} ${chg.doc.id}`);
            console.table({
              status: d?.status,
              needsReview: d?.needsReview,
              category: d?.category,
              matchedCategory: d?.categoryMatch?.category,
              matchedSubcategory: d?.categoryMatch?.subcategory,
              matchedSource: d?.categoryMatch?.source,
              source: d?.source,
              type: d?.type,
              vendor: d?.vendor,
              amount: d?.amount
            });
            console.groupEnd();
          }
        });
      });
    };
  }, [user]);

  const normalize = (v) => (typeof v === "string" ? v : v?.name ?? v?.label ?? v?.value ?? "");
  const isIncomeType  = (b) => (b?.type || "").toLowerCase() === "income"  || (b?.category || "").trim().toLowerCase() === "income";
  const isSavingsType = (b) => (b?.type || "").toLowerCase() === "savings";
  const isNonBudgetType = (b) => (b?.type || "").toLowerCase() === "non-budget";
  const isTransfersName = (b) => {
    const n = (b?.category || "").trim().toLowerCase();
    return n === "transfers & payments (not in budget)" || n === "transfers & reimbursements";
  };

  // Same idea as Transactions.jsx / Step3ConfirmMatches.jsx:
  // normalize for matching, but do NOT reorder/group/sort
  const toLabel = (v) => {
    if (v == null) return "";
    const s = String(v).trim();
    if (!s) return "";
    return s
      .split(" ")
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1).toLowerCase() : ""))
      .join(" ");
  };

  const normalizeBudgetsForMatching = (list = []) =>
    (Array.isArray(list) ? list : []).map((b) => {
      const cleanCategory = toLabel(b?.category ?? "");
      const cleanSubs = (b?.subcategories || []).map((s) => {
        const name = toLabel(typeof s === "string" ? s : (s?.name ?? s));
        return typeof s === "object" ? { ...s, name } : { name };
      });
      return { ...b, category: cleanCategory, subcategories: cleanSubs };
    });

  const matchingBudgets = React.useMemo(
    () => normalizeBudgetsForMatching(budgetDocsState),
    [budgetDocsState]
  );

  const dropdownBudgets = useMemo(() => {
    const list = Array.isArray(budgetDocsState) ? budgetDocsState : [];
    const sortCat = (a, b) => normalize(a?.category).localeCompare(normalize(b?.category), undefined, { sensitivity: "base" });
    const sortSubs = (subs = []) => [...subs].map(s => (typeof s === "string" ? { name: s } : { ...s, name: normalize(s) }))
                                        .sort((a, b) => normalize(a?.name).localeCompare(normalize(b?.name), undefined, { sensitivity: "base" }));

    const withSortedSubs = list.map(b => ({ ...b, subcategories: sortSubs(b.subcategories) }));

    const incomeGroup    = withSortedSubs.filter(isIncomeType).sort(sortCat);
    const savingsGroup   = withSortedSubs.filter(isSavingsType).sort(sortCat);
    const transfersGroup = withSortedSubs.filter(b => isTransfersName(b) || isNonBudgetType(b)).sort(sortCat);
    const expenseGroup   = withSortedSubs.filter(b => !isIncomeType(b) && !isSavingsType(b) && !isTransfersName(b) && !isNonBudgetType(b)).sort(sortCat);

    return [...incomeGroup, ...expenseGroup, ...savingsGroup, ...transfersGroup];
  }, [budgetDocsState]);

  const [needsActionCM, setNeedsActionCM] = useState([]); // current-month "needs action" txs
  const [projectedNet, setProjectedNet] = useState({ inc: 0, exp: 0, sav: 0, net: 0 });
  // Mobile breakpoint + projection info popover
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(max-width: 768px)").matches : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(max-width: 768px)");
    const onChange = (e) => setIsMobile(e.matches);
    try { mql.addEventListener("change", onChange); } catch { mql.addListener(onChange); }
    return () => {
      try { mql.removeEventListener("change", onChange); } catch { mql.removeListener(onChange); }
    };
  }, []);

  const [showProjInfo, setShowProjInfo] = useState(false);

  const [trendBadge, setTrendBadge] = useState(null); // "up" | "down" | "flat"

  // ── Needs Action modals/state ────────────────────────────────────────
  const auth = getAuth();
  const [showConfirmModal, setShowConfirmModal] = useState(null);
  const [showRuleModal, setShowRuleModal] = useState(null);

  // Render-noise control: count renders and print once per second (instead of logging on every render)
  const dashRenderBurstRef = useRef(0);
  dashRenderBurstRef.current += 1;
  useEffect(() => {
    if (!DEBUG_ACTIONS) return;
    const id = setInterval(() => {
      traceNA("Dashboard renders/sec", { count: dashRenderBurstRef.current });
      dashRenderBurstRef.current = 0;
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // Mobile Needs Action: action sheet + reassign + delete confirm
  const [needsActionMobileMenuTx, setNeedsActionMobileMenuTx] = useState(null);
  const [needsActionMobileExpandedId, setNeedsActionMobileExpandedId] = useState(null);
  const [showReassignModal, setShowReassignModal] = useState(null);
  useEffect(() => {
    if (!DEBUG_ACTIONS) return;
    traceNA("showReassignModal changed", {
      truthy: !!showReassignModal,
      id: showReassignModal?.id,
      vendor: showReassignModal?.vendor,
    });
  }, [showReassignModal]);

  const [showDeleteTx, setShowDeleteTx] = useState(null);

  const [needsActionMenuTx, setNeedsActionMenuTx] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(null);

  // Focused state-change tracing (signal > spam)
  useEffect(() => {
    if (!DEBUG_ACTIONS) return;
    traceNA("needsActionMenuTx changed", {
      truthy: !!needsActionMenuTx,
      id: needsActionMenuTx?.id,
      vendor: needsActionMenuTx?.vendor,
    });
  }, [needsActionMenuTx]);

  useEffect(() => {
    if (!DEBUG_ACTIONS) return;
    traceNA("showRuleModal changed", {
      truthy: !!showRuleModal,
      id: showRuleModal?.tx?.id,
      vendor: showRuleModal?.tx?.vendor,
    });
  }, [showRuleModal]);

  // Loader while we scan & apply a new rule to other transactions
  const [applyingRule, setApplyingRule] = useState(false);
  const [applyingRuleCount, setApplyingRuleCount] = useState(0);

  // Needs Action UI state — mirror of BudgetBuilderManual
  const [showAllNeedsAction, setShowAllNeedsAction] = useState(false);
  const [draftChanges, setDraftChanges] = useState({});
  const [ruleEditedMap, setRuleEditedMap] = useState({});
  const [needsActionWithSuggestions, setNeedsActionWithSuggestions] = useState([]);
  const [suggestionsReady, setSuggestionsReady] = useState(false);

  // Needs Action hard-stop error message (blocks confirm / rule creation)
  const [needsActionInlineError, setNeedsActionInlineError] = useState(null);

  // Local-only edits for Needs Action rows (prevents auto-writing to Firestore)
  // Structure: { [txId]: { category: string, subcategory: string } }
  const [needsActionEdits, setNeedsActionEdits] = useState({});

  const getNeedsActionEdit = (id) => needsActionEdits?.[id] || null;

  const setNeedsActionEdit = (id, patch) => {
    setNeedsActionEdits((prev) => ({
      ...(prev || {}),
      [id]: { ...(prev?.[id] || {}), ...(patch || {}) },
    }));
  };

  const clearNeedsActionEdit = (id) => {
    setNeedsActionEdits((prev) => {
      const next = { ...(prev || {}) };
      delete next[id];
      return next;
    });
  };

  useEffect(() => {
    if (!needsActionInlineError) return;
    const t = setTimeout(() => setNeedsActionInlineError(null), 5000);
    return () => clearTimeout(t);
  }, [needsActionInlineError]);

  // Treat corrupted placeholder values as invalid selections (lazy cleanup)
  const isPlaceholderSubcategory = (v) => String(v ?? "") === "subcategory";

  const sanitizeNeedsActionSubcategory = (tx, sub) => {
    const raw = sub ?? "";
    if (
      isPlaceholderSubcategory(raw) ||
      isPlaceholderSubcategory(tx?.subcategory) ||
      isPlaceholderSubcategory(tx?.categoryMatch?.subcategory)
    ) {
      return "";
    }
    return String(raw || "");
  };

  // Budget validation helpers (active = exists in current dropdownBudgets)
  const isSubValidUnderCategory = (cat, sub) => {
    const c = String(cat || "");
    const s = String(sub || "");
    if (!c || !s) return false;

    const b = (dropdownBudgets || []).find(
      (x) => normalize(x?.category) === normalize(c)
    );
    if (!b) return false;

    return (b.subcategories || []).some((sc) => {
      const name = typeof sc === "string" ? sc : sc?.name;
      return normalize(name) === normalize(s);
    });
  };

  const isActiveBudgetSelection = (cat, sub) => {
    const c = String(cat || "");
    const s = String(sub || "");
    if (!c || !s) return false;
    return isSubValidUnderCategory(c, s);
  };

  // Mobile: how many Needs Action rows to show (progressive reveal)
  const [needsMobileCount, setNeedsMobileCount] = useState(5);

  // Reset the counter when list changes or when switching layouts
  useEffect(() => {
    setNeedsMobileCount(5);
    setNeedsActionMobileExpandedId(null);
  }, [isMobile, needsActionWithSuggestions]);

  // equal-height refs for paired cards
  const beatsRef = useRef(null);        // "Budget Beats — Current Month"
  const moversUpRef = useRef(null);     // "Top Movers — Trending Favorably"
  const missesRef = useRef(null);       // "Budget Misses — Current Month"
  const moversDownRef = useRef(null);   // "Top Movers — Trending Down"

  // Has the user linked any Plaid accounts yet?
  async function hasLinkedPlaid(db, uid) {
    // (A) Canonical path you asked about:
    try {
      const itemRef = doc(db, "users", uid, "plaid", "item");
      const itemSnap = await getDoc(itemRef);
      if (itemSnap.exists()) {
        const d = itemSnap.data() || {};
        // If you prefer stricter: require both item_id and access_token
        if (d.item_id && d.env) return true;
      }
    } catch (_) {}

    // (B) Back-compat: look for old collections if they exist
    try {
      const qItems = query(collection(db, "plaid_items"), where("userId", "==", uid), limit(1));
      const itemsSnap = await getDocs(qItems);
      if (!itemsSnap.empty) return true;
    } catch (_) {}

    try {
      const qItemsAlt = query(collection(db, "plaidItems"), where("userId", "==", uid), limit(1));
      const itemsSnapAlt = await getDocs(qItemsAlt);
      if (!itemsSnapAlt.empty) return true;
    } catch (_) {}

    // (C) Fallback heuristic: any Plaid-sourced transactions?
    try {
      const qTx = query(
        collection(db, "transactions"),
        where("userId", "==", uid),
        where("source", "in", ["plaid", "plaid-sync", "plaid-backfill", "plaid-root"]),
        limit(1)
      );
      const txSnap = await getDocs(qTx);
      if (!txSnap.empty) return true;
    } catch (_) {}

    return false;
  }

  // Auto backfill (silent) once per browser session when the user is linked
  useEffect(() => {
    if (!user || !accountsConnected) return;
    let cancelled = false;
    (async () => {
      try {
        // avoid spamming: once per 6 hours per browser
        const KEY = "trelli_last_auto_backfill_ms";
        const last = Number(localStorage.getItem(KEY) || 0);
        const now = Date.now();
        if (now - last < 6 * 60 * 60 * 1000) return;

        const result = await backfillPlaidRoot(14); // pull the recent couple weeks
        if (!cancelled) console.log("🤖 Auto-backfill:", result);
        localStorage.setItem(KEY, String(now));
      } catch (e) {
        if (!cancelled) console.warn("Auto-backfill skipped:", e?.message || e);
      }
    })();
    return () => { cancelled = true; };
  }, [user, accountsConnected]);

  // Expose a helper so you can run it from DevTools:
  useEffect(() => {
    window.trelliBackfill = async (days = 90) => {
      const u = getAuth().currentUser;
      if (!u) throw new Error("Not signed in");
      const idToken = await u.getIdToken();
      const res = await fetch(
        "https://us-central1-trelli-prod-473722.cloudfunctions.net/plaidBackfillRoot",
        {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` },
          body: JSON.stringify({ days }),
        }
      );
      const json = await res.json();
      console.log("🔁 trelliBackfill →", json);
      return json;
    };
  }, []);

  useEffect(() => {
    function equalize(aRef, bRef) {
      const a = aRef.current, b = bRef.current;
      if (!a || !b) return;
      // reset any previous minHeights before measuring
      a.style.minHeight = ""; b.style.minHeight = "";
      const max = Math.max(a.offsetHeight, b.offsetHeight);
      a.style.minHeight = `${max}px`;
      b.style.minHeight = `${max}px`;
    }

    // initial equalize (when content loads/changes)
    equalize(beatsRef, moversUpRef);
    equalize(missesRef, moversDownRef);

    // re-equalize on resize
    const onResize = () => {
      equalize(beatsRef, moversUpRef);
      equalize(missesRef, moversDownRef);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [
    // re-run when these change height/content
    beats, movers,
    cmRowsState, l3RowsState, loading
  ]);

  // Does this tx's matched (category, subcategory) exist in the current budget?
  const budgetHasCategory = (tx) => {
    if (!tx.matchedCategory || !tx.matchedSubcategory) return false;
    return (dropdownBudgets || []).some(
      (b) =>
        b.category === tx.matchedCategory &&
        (b.subcategories || []).some((s) =>
          (typeof s === "string" ? s : s.name) === tx.matchedSubcategory
        )
    );
  };

  // IDENTICAL predicate to BudgetBuilderManual (no needsReview here)
  const uncategorizedTransactions = useMemo(() => {
    const sourceRows = Array.isArray(allTxState) ? allTxState : [];
    return sourceRows.filter((tx) =>
      !["confirmed", "manual", "rule"].includes(String(tx.matchedSource || "").toLowerCase()) &&
      (
        String(tx.matchedSource || "").toLowerCase() === "suggested" ||
        !tx.matchedCategory ||
        !tx.matchedSubcategory ||
        !budgetHasCategory(tx)
      )
    );
  }, [allTxState, dropdownBudgets]);

  // Month-End Projections alert numbers — mirror the Needs Action table exactly
  const needsCountAll = (uncategorizedTransactions || []).length;
  const needsTotalAll = (uncategorizedTransactions || []).reduce(
    (s, r) => s + Math.abs(Number(r.amount || 0)),
    0
  );

  // ADD ▶ trace uncategorizedTransactions recalculation + reasons snapshot
  useEffect(() => {
    if (!DEBUG_NEEDS) return;
    const total = (allTxState || []).length;
    const list = uncategorizedTransactions || [];

    console.group(`[NeedsAction] recompute (${list.length}/${total})`);
    // show a quick reason summary in buckets
    const buckets = { suggested: 0, missingPair: 0, notInBudget: 0, other: 0 };
    const inBudget = (cat, sub) => (dropdownBudgets || []).some(
      b => b.category === (cat || "") &&
      (b.subcategories || []).some(s => (typeof s === "string" ? s : s.name) === (sub || ""))
    );

    list.slice(0, 50).forEach(tx => {
      const ms = String(tx.matchedSource || "").toLowerCase();
      let reason = "other";
      if (ms === "suggested" || ms === "pseudorule") reason = "suggested";
      else if (!tx.matchedCategory || !tx.matchedSubcategory) reason = "missingPair";
      else if (!inBudget(tx.matchedCategory, tx.matchedSubcategory)) reason = "notInBudget";
      buckets[reason] = (buckets[reason] || 0) + 1;
    });

    console.table(buckets);
    console.table(list.slice(0, 15).map(t => ({
      id: t.id,
      vendor: t.vendor,
      amt: t.amount,
      matchedSource: t.matchedSource || "",
      matchedCategory: t.matchedCategory || "",
      matchedSubcategory: t.matchedSubcategory || "",
      inBudget: inBudget(t.matchedCategory, t.matchedSubcategory),
    })));
    console.groupEnd();
  }, [uncategorizedTransactions, dropdownBudgets, allTxState]);

  // Debug: why each tx was included in uncategorizedTransactions
  useEffect(() => {
    if (!DEBUG_NEEDS) return;
    const why = (tx) => {
      const ms = String(tx.matchedSource || "").toLowerCase();
      if (["confirmed","manual","rule"].includes(ms)) return null;
      if (ms === "suggested") return "suggested (awaiting confirm)";
      if (!tx.matchedCategory || !tx.matchedSubcategory) return "missing matched category/sub";
      if (!dropdownBudgets?.length) return "no budgets loaded";
      const inBudget = dropdownBudgets.some(
        b => b.category === (tx.matchedCategory || "") &&
        (b.subcategories || []).some(s => (typeof s === "string" ? s : s.name) === (tx.matchedSubcategory || ""))
      );
      return inBudget ? null : "matched pair not found in current budgets";
    };

    console.groupCollapsed("[NeedsAction] uncategorizedTransactions");
    console.log("count:", uncategorizedTransactions.length, "of", (allTxState || []).length);
    console.table(
      uncategorizedTransactions.slice(0, 25).map(t => ({
        id: t.id,
        vendor: t.vendor,
        amt: t.amount,
        matchedCategory: t.matchedCategory,
        matchedSubcategory: t.matchedSubcategory,
        matchedSource: t.matchedSource,
        reason: why(t)
      }))
    );
    console.groupEnd();
  }, [DEBUG_NEEDS, uncategorizedTransactions, dropdownBudgets, allTxState]);

  useEffect(() => {
    let isCancelled = false;

    async function buildSuggestions() {

      const mapped = await Promise.all(
        (uncategorizedTransactions || []).map(async (tx) => {
          // Keep transaction shape aligned with Transactions page / Budget Wizard:
          // - Keep tx.category as an array (legacy Plaid category list)
          // - DO NOT manufacture bankCategory from tx.category[0]
          // - Ensure PFCv2 fields are present (from root or nested personal_finance_category)
          const txForSuggest = {
            ...tx,
            category: Array.isArray(tx.category) ? tx.category : (tx.category ? [tx.category] : []),

            // only pass bankCategory if it truly exists in your tx doc
            bankCategory: tx.bankCategory || tx.plaidCategory || "",

            // ensure the matcher can see PFCv2 (preferred path in getSuggestedCategory)
            categoryDetailed:
              tx.categoryDetailed ??
              tx.personal_finance_category?.detailed ??
              null,

            categoryPrimary:
              tx.categoryPrimary ??
              tx.personal_finance_category?.primary ??
              null,
          };

          const suggested = await getSuggestedCategory(txForSuggest, matchingBudgets);

          return {
            ...tx,
            suggestedCategory: suggested?.category || null,
            suggestedSubcategory: suggested?.subcategory || null,
          };
        })
      );

      // ✅ Sort Needs Action by date (newest → oldest), safely handling Timestamp / Date / string
      const txToMillis = (t) => {
        const d = t?.date;
        if (!d) return 0;

        if (typeof d === "number") return d;
        if (typeof d === "string") {
          const ms = Date.parse(d);
          return Number.isFinite(ms) ? ms : 0;
        }
        if (d instanceof Date) return d.getTime();
        if (typeof d.toDate === "function") return d.toDate().getTime(); // Firestore Timestamp
        if (typeof d.seconds === "number") return d.seconds * 1000; // Firestore Timestamp-like
        return 0;
      };

      const mappedSorted = [...mapped].sort((a, b) => txToMillis(b) - txToMillis(a));

      if (DEBUG_NEEDS) {
        console.groupCollapsed("[NeedsAction] Suggestions");
        console.log("budgetsForSuggest categories:", (dropdownBudgets || []).map(b => b.category));
        console.log("rows considered:", (uncategorizedTransactions || []).length);

        const sample = mappedSorted.slice(0, 25).map(tx => ({
          id: tx.id,
          vendor: (tx.vendor || "").toLowerCase(),
          plaidCat0: (Array.isArray(tx.category) && tx.category.length
            ? tx.category[0]
            : (tx.bankCategory || tx.plaidCategory || "")),
          amount: tx.amount,
          suggestedCategory: tx.suggestedCategory || "",
          suggestedSubcategory: tx.suggestedSubcategory || "",
          matchedSource: tx.matchedSource || "",
        }));
        console.table(sample);

        const noSug = sample.filter(s => !s.suggestedCategory || !s.suggestedSubcategory);
        if (noSug.length) {
          console.warn("[NeedsAction] No suggestion for some rows → check vendor/plaid category mapping");
          console.table(noSug);
        }
        console.groupEnd();
      }

      if (!isCancelled) {
        setNeedsActionWithSuggestions(mappedSorted);
        setSuggestionsReady(true);
      }
    }

    buildSuggestions();
    return () => { isCancelled = true; };
  }, [uncategorizedTransactions, matchingBudgets, dropdownBudgets]);

  useEffect(() => {
    const onErr = (e) => {
      console.error("[window.onerror]", e?.message || e);
    };
    const onRej = (e) => {
      console.error("[unhandledrejection]", e?.reason || e);
    };
    window.addEventListener("error", onErr);
    window.addEventListener("unhandledrejection", onRej);
    return () => {
      window.removeEventListener("error", onErr);
      window.removeEventListener("unhandledrejection", onRej);
    };
  }, []);

  // Fit the RuleCreator modal to mobile screens
  const ruleWrapRef = useRef(null);
  const [ruleScale, setRuleScale] = useState(1);

  useEffect(() => {
    if (!isMobile || !showRuleModal) return;

    function measure() {
      const wrap = ruleWrapRef.current;
      if (!wrap) return;
      // The first real child is the RuleCreator content
      const child = wrap.firstElementChild;
      if (!child) return;
      // Desired content width (unscaled)
      const desired = child.scrollWidth || child.getBoundingClientRect().width || 600;
      // Available width = viewport minus padding (24px total)
      const avail = Math.max(300, window.innerWidth - 24);
      const s = Math.min(1, avail / desired);
      setRuleScale(s);
    }

    // Measure now and on resize/rotation
    measure();
    window.addEventListener("resize", measure);
    const t = setTimeout(measure, 0); // ensure layout done

    return () => {
      window.removeEventListener("resize", measure);
      clearTimeout(t);
    };
  }, [isMobile, showRuleModal]);

  useEffect(() => {
    // On first load or return navigation, ensure we start at the top
    window.scrollTo(0, 0);
  }, []);

  // When the user clicks “Edit” (open RuleCreator prefilled)
  function handleCategoryEditOpen(id, category, subcategory) {
    setShowRuleModal({
      tx: cmRowsState.find((t) => t.id === id),
      initialCategory: category,
      initialSubcategory: subcategory,
    });
  }

  const handleDraftCategoryChange = (id, newCategory) => {
    setDraftChanges((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), draftCategory: newCategory }
    }));
  };

  const handleDraftSubcategoryChange = async (id, newSubcategory) => {
    const draft = draftChanges[id] || {};
    const tx = (needsRowsState || []).find((t) => t.id === id);
    if (!tx) return;

    const categoryToUse = draft.draftCategory || tx.matchedCategory || tx.suggestedCategory;
    await updateTransactionCategory(id, categoryToUse, newSubcategory, "manual");

    setDraftChanges((prev) => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  // minimal version — updates Firestore and all three in-memory arrays so the row disappears immediately
  async function updateTransactionCategory(id, category, subcategory, sourceType = "manual") {
    const ref = doc(db, "transactions", id);

    // --- optimistic patch helper shared by all local arrays ---
    const patch = (row) =>
      row.id === id
        ? {
            ...row,
            matchedCategory: category,
            matchedSubcategory: subcategory,
            matchedSource: sourceType === "rule" ? "rule" : "user",
            needsReview: false,
            reviewMessage: null,
          }
        : row;

    // Keep snapshots for rollback
    const prevAllTx        = allTxState;
    const prevCmRows       = cmRowsState;
    const prevCmRowsAll    = cmRowsAllState;
    const prevNeedsRows    = needsRowsState;
    const prevSuggestList  = needsActionWithSuggestions;

    // --- OPTIMISTIC UI: update everything immediately ---
    setAllTxState((prev) => prev.map(patch));
    setCmRowsState((prev) => prev.map(patch));
    setCmRowsAllState((prev) => prev.map(patch));
    setNeedsRowsState((prev) => prev.map(patch));
    setNeedsActionWithSuggestions((prev) => prev.filter((t) => t.id !== id));

    // ADD ▶ optimistic patch trace
    console.group("[TX] updateTransactionCategory (optimistic)");
    console.log("id:", id, "→", { category, subcategory, sourceType });
    console.log("arrays:", {
      allTxState: (allTxState || []).length,
      cmRowsState: (cmRowsState || []).length,
      cmRowsAllState: (cmRowsAllState || []).length,
      needsRowsState: (needsRowsState || []).length
    });
    console.groupEnd();

    try {
      await updateDoc(ref, {
        categoryMatch: {
          type: sourceType,             // "manual" | "rule" | "confirmed"
          category,
          subcategory,
          source: sourceType === "rule" ? "rule" : "user",
        },
        category,
        subcategory,
        needsReview: false,
        reviewMessage: null,
        // ✅ top-level fields for reporting
        status: "confirmed",
        type: sourceType,               // matches reportingCore accepted set
        confirmedAt: serverTimestamp(),
      });
      console.log("[TX] Firestore update OK:", id);
    } catch (err) {
      console.error("updateTransactionCategory failed, rolling back:", err);
      // --- ROLLBACK on error ---
      setAllTxState(prevAllTx);
      setCmRowsState(prevCmRows);
      setCmRowsAllState(prevCmRowsAll);
      setNeedsRowsState(prevNeedsRows);
      setNeedsActionWithSuggestions(prevSuggestList);
      alert("Sorry — we couldn’t save that change. Nothing was changed.");
    }
  }

  async function authedPost(path, body) {
    const user = auth?.currentUser;
    if (!user) throw new Error("Not signed in");
    const token = await user.getIdToken();

    const res = await fetch(path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body || {}),
    });

    const json = await res.json().catch(() => ({}));
    if (!res.ok) {
      const msg = json?.error || json?.message || `Request failed (${res.status})`;
      throw new Error(msg);
    }
    return json;
  }

  function getPlaidTxIdFromTx(tx) {
    return (
      tx?.plaid_tx_id ||
      tx?.plaidTxId ||
      tx?.transaction_id ||
      null
    );
  }

  async function deleteTransactionById(id) {
    if (!id) return;

    // Try to find the full tx object so we can detect plaid_tx_id
    const tx =
      (allTxState || []).find((t) => t.id === id) ||
      (cmRowsState || []).find((t) => t.id === id) ||
      (cmRowsAllState || []).find((t) => t.id === id) ||
      (needsRowsState || []).find((t) => t.id === id) ||
      (needsActionWithSuggestions || []).find((t) => t.id === id) ||
      null;

    const plaidTxId = getPlaidTxIdFromTx(tx);

    // snapshots for rollback
    const prevAllTx       = allTxState;
    const prevCmRows      = cmRowsState;
    const prevCmRowsAll   = cmRowsAllState;
    const prevNeedsRows   = needsRowsState;
    const prevSuggestList = needsActionWithSuggestions;

    // optimistic remove everywhere
    setAllTxState((prev) => (prev || []).filter((t) => t.id !== id));
    setCmRowsState((prev) => (prev || []).filter((t) => t.id !== id));
    setCmRowsAllState((prev) => (prev || []).filter((t) => t.id !== id));
    setNeedsRowsState((prev) => (prev || []).filter((t) => t.id !== id));
    setNeedsActionWithSuggestions((prev) => (prev || []).filter((t) => t.id !== id));

    try {
      if (plaidTxId) {
        // ✅ Plaid-backed: tombstone + delete root doc (server-side enforcement)
        await authedPost("/deleteTransaction", {
          plaid_tx_id: plaidTxId,
          source: "dashboard_needs_action_delete",
        });
      } else {
        // Manual/non-Plaid: delete root doc directly
        const ref = doc(db, "transactions", id);
        await deleteDoc(ref);
      }
    } catch (err) {
      console.error("deleteTransactionById failed, rolling back:", err);
      setAllTxState(prevAllTx);
      setCmRowsState(prevCmRows);
      setCmRowsAllState(prevCmRowsAll);
      setNeedsRowsState(prevNeedsRows);
      setNeedsActionWithSuggestions(prevSuggestList);
      alert("Sorry — we couldn’t delete that transaction. Nothing was changed.");
    }
  }

  // Apply a just-saved rule to other matching "Needs Action" transactions.
  // Optimistic local update first; then commit in a Firestore batch.
  async function applyRuleToOtherMatches({ vendor, vendorMatchType = "contains", plaidCategory = "", category, subcategory }) {
    // Guard
    const userId = auth.currentUser?.uid;
    if (!userId || !category || !subcategory) return;

    // ADD ▶ introspect rule + candidates
    console.group("[Rules] applyRuleToOtherMatches");
    console.log("match vendor:", (vendor || "").toLowerCase(), "vendorMatchType:", vendorMatchType);
    console.log("match plaidCategory:", (plaidCategory || "").toLowerCase());

    // Snapshot for rollback
    const prevAllTx        = allTxState;
    const prevCmRows       = cmRowsState;
    const prevCmRowsAll    = cmRowsAllState;
    const prevNeedsRows    = needsRowsState;
    const prevSuggestList  = needsActionWithSuggestions;

    // Predicate: keep it aligned with how you save rules from confirm
    const wantVendor = String(vendor || "").toLowerCase();
    const wantPlaid  = String(plaidCategory || "").toLowerCase();

    const isNeedsActionTx = (tx) => {
      const ms = String(tx.matchedSource || "").toLowerCase();
      const notLocked = !["confirmed", "manual", "rule", "user"].includes(ms);
      const missingPair = !tx.matchedCategory || !tx.matchedSubcategory;
      // also include suggested/pseudoRule rows
      const flagged = ms === "suggested" || ms === "pseudorule" || missingPair;
      return notLocked && flagged;
    };

    const matchesRule = (tx) => {
      const v = String(tx.vendor || "").toLowerCase();
      const p0 = (
        (Array.isArray(tx.category) && tx.category.length ? tx.category[0] : (tx.bankCategory || tx.plaidCategory || ""))
      ).toLowerCase();

      if (wantVendor) {
        if (vendorMatchType === "contains") {
          if (!v.includes(wantVendor)) return false;
        } else {
          if (v !== wantVendor) return false; // simple fallback for "exact"
        }
      }
      if (wantPlaid && p0 !== wantPlaid) return false;

      return true;
    };

    // Build list of affected ids (limit to keep UI snappy if needed)
    const candidates = (allTxState || []).filter((tx) => isNeedsActionTx(tx) && matchesRule(tx));
    console.log("candidates:", candidates.length);
    console.table(candidates.slice(0, 25).map(tx => ({
      id: tx.id,
      vendor: (tx.vendor || "").toLowerCase(),
      plaid0: (Array.isArray(tx.category) && tx.category[0]) || (tx.bankCategory || tx.plaidCategory || ""),
      matchedSource: tx.matchedSource || "",
      matchedCategory: tx.matchedCategory || "",
      matchedSubcategory: tx.matchedSubcategory || "",
    })));
    console.groupEnd();
    // Remove the transaction you *just* confirmed (already updated)
    // Not strictly required, harmless if not present.
    // const excludeId = showConfirmModal?.id; // (we already closed the modal before calling)
    // const targets = candidates.filter(c => c.id !== excludeId);
    const targets = candidates;

    if (targets.length === 0) return;

    setApplyingRule(true);
    setApplyingRuleCount(targets.length);

    // Optimistic patch across all local arrays and the suggestions render
    const patchMany = (row) =>
      targets.some(t => t.id === row.id)
        ? {
            ...row,
            matchedCategory: category,
            matchedSubcategory: subcategory,
            matchedSource: "rule",
            needsReview: false,
            reviewMessage: null,
          }
        : row;

    setAllTxState((prev) => prev.map(patchMany));
    setCmRowsState((prev) => prev.map(patchMany));
    setCmRowsAllState((prev) => prev.map(patchMany));
    setNeedsRowsState((prev) => prev.map(patchMany));
    setNeedsActionWithSuggestions((prev) => prev.filter((t) => !targets.some(x => x.id === t.id)));

    try {
      // Commit to Firestore in a batch
      const batch = writeBatch(db);
      for (const tx of targets) {
        batch.update(doc(db, "transactions", tx.id), {
          categoryMatch: { type: "rule", category, subcategory, source: "rule" },
          category,
          subcategory,
          needsReview: false,
          reviewMessage: null,
          // ✅ top-level fields for reporting
          status: "confirmed",
          type: "rule",
          confirmedAt: serverTimestamp(),
        });
      }
      await batch.commit();
    } catch (err) {
      console.error("applyRuleToOtherMatches failed, rolling back:", err);
      // Rollback UI
      setAllTxState(prevAllTx);
      setCmRowsState(prevCmRows);
      setCmRowsAllState(prevCmRowsAll);
      setNeedsRowsState(prevNeedsRows);
      setNeedsActionWithSuggestions(prevSuggestList);
      alert("We couldn’t apply that rule to other transactions. No changes were saved.");
    } finally {
      setApplyingRule(false);
      setApplyingRuleCount(0);
    }
  }

  function buildConfirmModalTx(tx, suggested) {
    return {
      ...tx,
      matchedCategory: tx.matchedCategory || suggested?.category || "",
      matchedSubcategory: tx.matchedSubcategory || suggested?.subcategory || "",
      suggestedCategory: suggested?.category || "",
      suggestedSubcategory: suggested?.subcategory || "",
    };
  }

  function buildDetailsTx(tx) {
    const accountLabel =
      accountNameById?.[tx.accountId] ||
      accountNameById?.[tx.account_id] ||
      tx.accountName ||
      tx.account_name ||
      tx.accountId ||
      tx.account_id ||
      "";

    const bankCat = Array.isArray(tx.category)
      ? (tx.category[0] || "")
      : (tx.bankCategory || tx.plaidCategory || "");

    return {
      ...tx,
      date: formatDate(tx.date),
      accountName: accountLabel,
      bankCategory: bankCat,
      memo: tx.memo || "",
    };
  }

  async function handleConfirmAndSaveRule(modalTx) {
    const category = modalTx.matchedCategory || modalTx.suggestedCategory;
    const subcategory = modalTx.matchedSubcategory || modalTx.suggestedSubcategory;
    if (!category) return;

    // 1) confirm this tx
    await updateTransactionCategory(modalTx.id, category, subcategory, "rule");

    // 2) save rule exactly like Builder
    const userId = auth.currentUser?.uid;        // ← add this line
    await saveUserRule({
      userId,
      vendor: (modalTx.vendor || "").toLowerCase(),
      vendorMatchType: "contains",
      plaidCategory: ((modalTx.category || [])[0] || modalTx.bankCategory || "").toLowerCase(),
      category,
      subcategory,
    });

    await applyRuleToOtherMatches({
      userId: auth.currentUser?.uid,
      vendor: (modalTx.vendor || "").toLowerCase(),
      vendorMatchType: "contains",
      plaidCategory: ((modalTx.category || [])[0] || modalTx.bankCategory || "").toLowerCase(),
      category,
      subcategory,
    });

    setRuleEditedMap((prev) => ({
      ...prev,
      [modalTx.id]: { category, subcategory }
    }));

    setShowConfirmModal(null);
  }

  async function handleConfirmWithoutRule(modalTx) {
    // Confirm what the user actually selected (matched fields),
    // fall back to suggested only if matched is missing.
    const category =
      modalTx.matchedCategory ||
      modalTx.category ||
      modalTx.suggestedCategory ||
      "";

    const subcategory =
      modalTx.matchedSubcategory ||
      modalTx.subcategory ||
      modalTx.suggestedSubcategory ||
      "";

    if (!category) return;

    await updateTransactionCategory(modalTx.id, category, subcategory, "confirmed");
    setShowConfirmModal(null);
  }

  // Called by RuleCreator on save
  async function handleSaveRule(draft) {
    const { tx } = showRuleModal || {};
    if (!tx) return;

    const user = auth.currentUser;
    if (!user) return;

    // ✅ Use the edited vendor coming from RuleCreator (fallback to tx.vendor)
    const finalVendor = String(draft?.vendor ?? tx.vendor ?? "").trim();

    await saveUserRule({
      userId: user.uid,

      // ✅ FIX: save edited vendor
      vendor: finalVendor,

      category: draft.category,
      subcategory: draft.subcategory,

      amount: draft.amount ?? tx.amount,
      plaidCategory: draft.plaidCategory ?? ((tx.category || [])[0] || ""),

      // (Optional but recommended) preserve additional matching fields from the modal
      vendorMatchType: draft.vendorMatchType,
      amountMatchType: draft.amountMatchType,
      amountMatchValue: draft.amountMatchValue,
      dateMatchType: draft.dateMatchType,
      dateMatchValue: draft.dateMatchValue,
      transactionType: draft.transactionType,
      accountName: draft.accountName,
      mcc: draft.mcc,
    });

    // also persist the change to the transaction match (count it as rule-based)
    await updateTransactionCategory(tx.id, draft.category, draft.subcategory, "rule");

    await applyRuleToOtherMatches({
      userId: auth.currentUser?.uid,
      vendor: finalVendor.toLowerCase(),

      // ✅ respect how the user set vendor matching (fallback to contains)
      vendorMatchType: draft.vendorMatchType || "contains",

      plaidCategory: String(draft.plaidCategory ?? ((tx.category || [])[0] || "")).toLowerCase(),
      category: draft.category,
      subcategory: draft.subcategory,
    });

    setShowRuleModal(null);
  }

  // 🔄 Fetch the link token from Firebase function (PROD + ID token)
  const fetchLinkToken = async (mode = "create") => {
    console.log("Get Link Token clicked");
    try {
      const user = auth.currentUser;
      if (!user) {
        alert("Please sign in first.");
        return;
      }
      const idToken = await user.getIdToken();

      const res = await fetch(
        "https://us-central1-trelli-prod-473722.cloudfunctions.net/createLinkToken",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ mode }), // "create" or "update"
        }
      );

      if (!res.ok) {
        console.error("createLinkToken failed:", await res.text());
        alert("Failed to create Link token.");
        return;
      }

      const { link_token } = await res.json();
      console.log("createLinkToken →", link_token);
      setLinkToken(link_token);
    } catch (error) {
      console.error("Error fetching link token:", error);
      alert("Error starting Plaid Link.");
    }
  };

  async function backfillPlaidRoot(days = 90) {
    const user = getAuth().currentUser;
    if (!user) throw new Error("Not signed in");
    const idToken = await user.getIdToken();

    const res = await fetch(
      "https://us-central1-trelli-prod-473722.cloudfunctions.net/plaidBackfillRoot",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({ days }),
      }
    );

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`plaidBackfillRoot failed: ${text}`);
    }
    return res.json(); // { ok, written, range_days }
  }

  // 📎 Launch Plaid Link once token is ready
  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (public_token, metadata) => {
      try {
        const user = auth.currentUser;
        const idToken = await user.getIdToken();

        // 1) Exchange the Link public token
        const exch = await fetch(
          "https://us-central1-trelli-prod-473722.cloudfunctions.net/exchangePublicToken",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({ public_token }),
          }
        );

        if (!exch.ok) {
          console.error("exchangePublicToken failed:", await exch.text());
          alert("Linked, but saving tokens failed.");
          return;
        }

        console.log("exchangePublicToken → ok", metadata);
        setAccountsConnected(true);
        setPlaidBroken(false);
        setPlaidErrCode(null);

        // 2) Backfill into /transactions (root, normalized shape your UI reads)
        const backfill = await backfillPlaidRoot(90);
        console.log("plaidBackfillRoot →", backfill);

        // 3) Quick re-query so the panel updates immediately (same logic you use in useEffect)
        {
          const q = query(collection(db, "transactions"), where("userId", "==", user.uid));
          const snap = await getDocs(q);
          const data = snap.docs.map((d) => {
            const raw = d.data() || {};
            return {
              id: d.id,
              ...raw,
              category: Array.isArray(raw.category) ? raw.category : (raw.category ? [raw.category] : []),
              matchedCategory: raw.categoryMatch?.category || null,
              matchedSubcategory: raw.categoryMatch?.subcategory || null,
              matchedSource: raw.categoryMatch?.source || raw.matchedSource || null,
              date: raw.date?.toDate ? raw.date.toDate().toISOString() : raw.date,
            };
          });
          setAllTxState(data);
        }

        alert(`✅ Bank connected and ${backfill.written} transactions imported.`);

      } catch (e) {
        console.error(e);
        alert("Unexpected error exchanging token or importing transactions.");
      }
    },
    onExit: (err, metadata) => {
      if (err) console.warn("Plaid exit:", err, metadata);
    },
  });

  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u || null);
      if (!u) return;

      setLoading(true);
      try {
        // 1) budgets (for classification index inside reportingCore)
        const { budgetDocs } = await loadBudgetDocs(db, u.uid);

        // Coerce amounts -> numbers so downstream math is reliable
        const coercedBudgetDocs = (budgetDocs || []).map(b => ({
          ...b,
          subcategories: (b.subcategories || []).map(s => ({
            ...s,
            amount: Number(s.amount ?? 0)
          })),
        }));

        setBudgetDocsState(coercedBudgetDocs);

        console.groupCollapsed("[DBG:Budgets] coercedBudgetDocs");
        console.log("count:", coercedBudgetDocs.length);
        console.table(coercedBudgetDocs.slice(0,10).map(b => ({
          type: b.type, category: b.category,
          subs: (b.subcategories||[]).length,
          subAmtSum: (b.subcategories||[]).reduce((s,sb)=>s+Number(sb.amount??0),0)
        })));
        console.groupEnd();

        // ⬇️ Fetch ALL user transactions (same feed BudgetBuilderManual uses)
        {
          const q = query(collection(db, "transactions"), where("userId", "==", u.uid));
          const snap = await getDocs(q);
          const data = snap.docs.map((d) => {
            const raw = d.data() || {};
            return {
              id: d.id,
              ...raw,
              // Normalize fields exactly like BudgetBuilderManual
              category: Array.isArray(raw.category) ? raw.category : (raw.category ? [raw.category] : []),
              matchedCategory: raw.categoryMatch?.category || null,
              matchedSubcategory: raw.categoryMatch?.subcategory || null,
              matchedSource: raw.categoryMatch?.source || raw.matchedSource || null,
              date: raw.date?.toDate ? raw.date.toDate().toISOString() : raw.date,
            };
          });
          setAllTxState(data);

          // Hide the “Connect accounts” box if the user already linked Plaid
          try {
            const linked = await hasLinkedPlaid(db, u.uid);
            setAccountsConnected(linked || (data && data.length > 0));
          } catch (e) {
            console.warn("Linked check failed; falling back to TX length.", e);
            setAccountsConnected((data && data.length > 0) || false);
          }
        }

        if (DEBUG_NEEDS) {
          console.groupCollapsed("[NeedsAction] Firestore → allTxState");
          console.log("count:", (allTxState || []).length);
          console.table(
            (allTxState || []).slice(0, 15).map(d => ({
              id: d.id,
              vendor: d.vendor,
              amount: d.amount,
              plaid_cat0: Array.isArray(d.category) ? d.category[0] : "",
              matchedCategory: d.matchedCategory,
              matchedSubcategory: d.matchedSubcategory,
              matchedSource: d.matchedSource,
              needsReview: d.needsReview || false,
              reviewMessage: d.reviewMessage || "",
              // raw categoryMatch snapshot
              cm_source: d.categoryMatch?.source,
              cm_cat: d.categoryMatch?.category,
              cm_sub: d.categoryMatch?.subcategory,
            }))
          );
          console.groupEnd();
        }

        // 2) Current Month totals
        const { start: cmStart, end: cmEnd } = currentMonthRange();
        const { rows: cmRows } = await loadClassifiedRows(
          db, u.uid, { start: cmStart, end: cmEnd, confirmedOnly: true }, coercedBudgetDocs
        );
        setCmTotals(sumTotals(cmRows));

        // DEBUG: Current month totals from confirmed rows
        console.groupCollapsed("[DBG:CM] cmTotals (confirmed only)");
        console.log("rows.len:", cmRows.length, "sumTotals:", sumTotals(cmRows));
        console.groupEnd();

        // keep cm rows for charts
        setCmRowsState(cmRows);

        console.groupCollapsed("[DBG:CM] cmRows (classified, confirmed)");
        console.log("len:", cmRows.length);
        console.table(cmRows.slice(0,12).map(r => ({
          date: r.date?.toISOString?.() || r.date,
          _type: r._type, cat: r.category, sub: r.subcategory,
          amt: r.amount
        })));
        console.groupEnd();

        // fetch all current-month rows (confirmed + unconfirmed) for Needs Action
        const { rows: cmAllRows } = await loadClassifiedRows(
          db, u.uid, { start: cmStart, end: cmEnd, confirmedOnly: false }, coercedBudgetDocs
        );
        setCmRowsAllState(cmAllRows);

        // Needs Action: fetch a broader window (last 180 days), unconfirmed INCLUDED
        {
          const today = new Date();
          const startWide = new Date(today);
          startWide.setDate(startWide.getDate() - 180);

          const { rows: wideRows } = await loadClassifiedRows(
            db, u.uid, { start: startWide, end: new Date(), confirmedOnly: false }, coercedBudgetDocs
          );

          setNeedsRowsState(wideRows || []);
        }

        // 🔎 Needs Action = tx that are unconfirmed or missing a valid category/subcategory present in budgets
        {
          const budgetMap = new Map(
            (coercedBudgetDocs || []).map(b => [String(b.category || ""), new Set((b.subcategories || []).map(s => s.name))])
          );
          const needs = (cmRows || []).filter(tx => {
            const hasMatch = !!(tx?.matchedCategory && tx?.matchedSubcategory);
            const catOk = budgetMap.has(tx?.matchedCategory || "");
            const subOk = catOk && budgetMap.get(tx.matchedCategory).has(tx.matchedSubcategory || "");
            const flagged = !!tx?.needsReview || !!tx?.reviewMessage;
            return flagged || !hasMatch || !catOk || !subOk;
          });
          setNeedsActionCM(needs);
        }

        // 📈 Projected Month End Net (EXACTLY like BudgetBuilderManual “Projected Month End”)
        {
          // 1) Build ACTUAL-TO-DATE per subcategory for the current month rows
          //    Income: signed sums   |   Expense & Savings: magnitude sums (refunds reduce)
          const actualBySub = new Map(); // key = "Category|Sub"

          for (const r of (cmRows || [])) {
            const t = String(r?._type || "");
            if (t !== "income" && t !== "expense" && t !== "savings") continue;

            const cat = r?.category || "";
            const sub = r?.subcategory || "";
            const key = `${cat}|${sub}`;

            if (t === "income") {
              const prev = Number(actualBySub.get(key) || 0);
              actualBySub.set(key, prev + Number(r.amount || 0)); // signed (negatives reduce income)
            } else {
              const prev = Number(actualBySub.get(key) || 0);
              // addToExpenseMagnitude(prev, amt) should ADD positive debits, SUBTRACT refunds
              actualBySub.set(key, addToExpenseMagnitude(prev, Number(r.amount || 0)));
            }
          }

          // 2) For each budget subcategory, compute:
          //    Projected = monthlyBudget + (actualToDate - expectedToDate)
          //    using getSubcategoryStatus meta fields, to match BudgetBuilderManual
          const projectForType = (wantType) => {
            const isInc = wantType === "income";
            let total = 0;

            // Use the SAME budgets you coerced above so amounts are numeric
            for (const bud of (coercedBudgetDocs || [])) {
              const type = String(bud?.type || "").toLowerCase();
              if (type !== wantType) continue;

              const cat = bud?.category || "";
              const subs = Array.isArray(bud?.subcategories) ? bud.subcategories : [];

              for (const sub of subs) {
                const key = `${cat}|${sub?.name || ""}`;

                // actual so far: signed for income; "magnitude with refunds reducing" for outflows
                const actualSoFar = Number(actualBySub.get(key) || 0);

                // status pacing (same helper used elsewhere)
                const { meta } = subStatus({ subcategory: sub, actual: actualSoFar, isIncome: isInc });

                // monthly budget is the subcategory amount (NOT the entire category sum)
                const monthlyBudget = Number(sub?.amount ?? 0);

                // prefer expectedToDate if present; fall back to expectedForRollup
                const expectedToDate = Number(
                  (meta && (meta.expectedToDate ?? meta.expectedForRollup)) || 0
                );

                // Projected = monthly budget + (actual to date - expected to date)
                const projectedSub = monthlyBudget + (actualSoFar - expectedToDate);

                total += projectedSub;
              }
            }
            return total;
          };

          const incProjected = projectForType("income");
          const expProjected = projectForType("expense");
          const savProjected = projectForType("savings");

          setProjectedNet({
            inc: incProjected,
            exp: expProjected,
            sav: savProjected,
            net: incProjected - (expProjected + savProjected),
          });
          console.groupCollapsed("[DBG:PROJ:CM] projected month end");
          console.log("actualBySub.size:", actualBySub.size);
          console.table(Array.from(actualBySub.entries()).slice(0,15).map(([k,v]) => ({ key:k, actualSoFar:v })));
          console.log("projected:", {
            inc: incProjected, exp: expProjected, sav: savProjected,
            net: incProjected - (expProjected + savProjected)
          });
          console.groupEnd();
        }

        // 2b) Current Month BvA (expected-to-date pacing for budget)
        const cmMonths = monthKeysBetween(cmStart, cmEnd); // one key
        const cmBvaOut = await computeBVA({
          months: cmMonths,
          rows: cmRows,
          budgetDocs: coercedBudgetDocs,
          filter: {},
          includeCurrentMonthMTD: true,
          db,
          uid: u.uid,
        });
        console.groupCollapsed("[DBG:BVA:CM] computeBVA input/output");
        console.log("months:", cmMonths, "rows.len:", cmRows.length, "budgets:", coercedBudgetDocs.length);
        console.table((cmBvaOut?.rows || []).slice(0,10));
        console.groupEnd();
        {
          // Build a helper to get MTD pacing (“MTD Budgeted”) consistently for any type
          const expectedToDateForSub = (sub, isIncome) => {
            const { meta } = getSubcategoryStatus({ subcategory: sub, actual: 0, isIncome });
            return Number(meta?.expectedForRollup || 0);
          };

          // MTD Budgeted (pacing) — sum expectedToDate across budget subs
          let bInc = 0, bExp = 0, bSav = 0;
          for (const b of coercedBudgetDocs) {
            const type = String(b?.type || "").toLowerCase();
            const subs = Array.isArray(b?.subcategories) ? b.subcategories : [];
            if (type === "income") {
              for (const sub of subs) bInc += expectedToDateForSub(sub, true);
            } else if (type === "expense") {
              for (const sub of subs) bExp += expectedToDateForSub(sub, false);
            } else if (type === "savings") {
              for (const sub of subs) bSav += expectedToDateForSub(sub, false);
            }
          }

          // Actuals (MTD) — straight from current-month rows, matching Builder rules
          const aInc = cmRows
            .filter(r => r?._type === "income")
            .reduce((s, r) => s + Number(r.amount || 0), 0); // signed

          const aExp = cmRows
            .filter(r => r?._type === "expense")
            .reduce((s, r) => addToExpenseMagnitude(s, r.amount), 0); // magnitude (refunds reduce)

          const aSav = cmRows
            .filter(r => r?._type === "savings")
            .reduce((s, r) => addToExpenseMagnitude(s, r.amount), 0); // magnitude

          const bNet = bInc - (bExp + bSav);
          const aNet = aInc - (aExp + aSav);

          setBvaCm({
            bInc, bExp, bSav,
            bNet,
            aInc, aExp, aSav,
            aNet,
            vNet: aNet - bNet
          });
          console.log("[DBG:BVA:CM] totals",
            { bInc, bExp, bSav, bNet, aInc, aExp, aSav, aNet, vNet: aNet - bNet }
          );
        }

        // 2c) Top Beats & Misses (Current Month)
        // Build budget-by-category/subcategory using expected-to-date pacing,
        // and actual magnitudes for the same period.
        const catB = new Map(); // label -> { type, b }
        const subB = new Map(); // "Category: Sub" -> { type, b }
        const expectedToDateForSub = (sub, isIncome) => {
          const { meta } = getSubcategoryStatus({ subcategory: sub, actual: 0, isIncome });
          return Number(meta?.expectedForRollup || 0);
        };
        for (const b of coercedBudgetDocs) {
          const type = String(b?.type || "").toLowerCase(); // income | expense | savings | non-budget
          const category = b?.category || "";
          if (!category || type === "non-budget") continue;
          const subs = Array.isArray(b?.subcategories) ? b.subcategories : [];
          for (const sub of subs) {
            const name = sub?.name || "";
            const amt = expectedToDateForSub(sub, type === "income");
            const catLabel = category;
            const subLabel = name ? `${category}: ${name}` : `${category}: (Uncategorized)`;
            if (!catB.has(catLabel)) catB.set(catLabel, { type, b: 0 });
            if (!subB.has(subLabel)) subB.set(subLabel, { type, b: 0 });
            catB.get(catLabel).b += Number(amt) || 0;
            subB.get(subLabel).b += Number(amt) || 0;
          }
        }
        const catA = new Map(); // label -> { type, a }
        const subA = new Map(); // "Category: Sub" -> { type, a }
        for (const r of cmRowsState.filter(isBudgetRow)) {
          const t = r._type; // income | expense | savings
          const catLabel = catLabelOf(r);
          const subLabel = subLabelOf(r);
          let next;
          if (t === "income") {
            next = Number(r.amount || 0); // signed income (negatives reduce)
          } else {
            // outflow magnitude with refunds reducing
            const curC = catA.get(catLabel)?.a || 0;
            const curS = subA.get(subLabel)?.a || 0;
            const aC = addToExpenseMagnitude(curC, r.amount);
            const aS = addToExpenseMagnitude(curS, r.amount);
            if (!catA.has(catLabel)) catA.set(catLabel, { type: t, a: 0 });
            if (!subA.has(subLabel)) subA.set(subLabel, { type: t, a: 0 });
            catA.get(catLabel).a = aC;
            subA.get(subLabel).a = aS;
            continue;
          }
          if (!catA.has(catLabel)) catA.set(catLabel, { type: t, a: 0 });
          if (!subA.has(subLabel)) subA.set(subLabel, { type: t, a: 0 });
          catA.get(catLabel).a += next;
          subA.get(subLabel).a += next;
        }
        function buildBeatMiss(catMapB, catMapA) {
          const beats = []; // favorable
          const misses = []; // needs attention
          for (const [label, bObj] of catMapB.entries()) {
            const b = Number(bObj.b || 0);
            const t = String(bObj.type || "").toLowerCase();
            const a = Number(catMapA.get(label)?.a || 0);
            if (!b && !a) continue;
            if (t === "income") {
              const beat = a - b;          // actual >= budget is favorable
              const miss = b - a;          // underperform
              if (beat > 0) beats.push({ label, amount: beat });
              if (miss > 0) misses.push({ label, amount: miss });
            } else { // expense or savings (outflow)
              const beat = b - a;          // underspend is favorable
              const miss = a - b;          // overspend
              if (beat > 0) beats.push({ label, amount: beat });
              if (miss > 0) misses.push({ label, amount: miss });
            }
          }
          beats.sort((a,b) => b.amount - a.amount);
          misses.sort((a,b) => b.amount - a.amount);
          return { beatTop: beats.slice(0,3), missTop: misses.slice(0,3) };
        }
        const catRes = buildBeatMiss(catB, catA);
        const subRes = buildBeatMiss(subB, subA);
        setBeats({
          beatCatsTop: catRes.beatTop, missCatsTop: catRes.missTop,
          beatSubsTop: subRes.beatTop, missSubsTop: subRes.missTop
        });

        // 3) Last 3 full completed months (combined)
        const { start: l3Start, end: l3End } = last3FullMonthsCombined();

        console.log(
          "[DBG:L3] range",
          l3Start?.toISOString?.() ?? l3Start,
          "→",
          l3End?.toISOString?.() ?? l3End,
          "budgets(coerced)=",
          coercedBudgetDocs.length
        );

        const { rows: l3Rows } = await loadClassifiedRows(
          db,
          u.uid,
          { start: l3Start, end: l3End, confirmedOnly: true },
          coercedBudgetDocs
        );
        console.log("[DBG:L3] l3Rows.len =", l3Rows.length);
        console.table(
          l3Rows.slice(0, 5).map(r => ({
            date: r.date?.toISOString?.() || r.date,
            type: r._type,
            cat: r.category,
            sub: r.subcategory,
            amt: r.amount
          }))
        );
        setLm3Totals(sumTotals(l3Rows));

        // keep l3 rows for charts
        setL3RowsState(l3Rows);

        // 3b) Last 3 full months BvA (completed months → snapshot/live budgets)
        const l3Months = monthKeysBetween(l3Start, l3End);
        console.log("[DBG:L3] months =", l3Months, "rows.len =", l3Rows.length);
        const l3BvaOut = await computeBVA({
          months: l3Months,
          rows: l3Rows,
          budgetDocs: coercedBudgetDocs,
          filter: {},
          includeCurrentMonthMTD: false,
          db,
          uid: u.uid,
        });
        {
          // Base totals from computeBVA
          let bInc = 0, bExp = 0, aInc = 0, aExp = 0;
          for (const r of l3BvaOut.rows) {
            bInc += Number(r.bIncome || 0);
            bExp += Number(r.bExpense || 0);
            aInc += Number(r.aIncome || 0);
            aExp += Number(r.aExpense || 0);
          }
          // Savings (Actual) from rows, (Budget) best-effort from BVA rows if present; else 0
          const aSav = l3Rows
            .filter(r => r?._type === "savings")
            .reduce((s, r) => addToExpenseMagnitude(s, r.amount), 0);
          // Try to read savings budget from BVA rows if a savings-specific field is present.
          // (If your computeBVA doesn’t emit it, this safely stays 0 and Net remains accurate.)
          let bSav = 0;
          for (const r of l3BvaOut.rows) {
            bSav += Number(r.bSavings || r.bSav || 0);
          }
          const bNet = bInc - (bExp + bSav);
          const aNet = aInc - (aExp + aSav);
          setBvaL3({ bInc, bExp, bSav, bNet, aInc, aExp, aSav, aNet, vNet: aNet - bNet });
          console.log("[DBG:L3] totals", {
            bInc, bExp, bSav, bNet,
            aInc, aExp, aSav, aNet,
            vNet: aNet - bNet
          });
        }

        // 3c) Top Movers (Last 3 Full Months): trend by month for categories + subcategories
      const { months: l3MonthsArr } = last3FullMonthsRange(); // [{start,end},...], but we only need the 3 month keys for trend
      const periodKeys = monthKeysBetween(l3Start, l3End);
      const rowsF3 = l3Rows.filter(isBudgetRow);
      const catMap = new Map(); // cat -> Map(YYYY-MM -> signed)
      const subMap = new Map(); // "Cat: Sub" -> Map(YYYY-MM -> signed)
      for (const r of rowsF3) {
        const mk = ymKey(new Date(r.date.getFullYear(), r.date.getMonth(), 1));
        const signed = signedForNet(r); // income keeps sign, expense/savings keep refunds positive, spends negative
        const cat = r.category || "(Uncategorized)";
        if (!catMap.has(cat)) catMap.set(cat, new Map());
        catMap.get(cat).set(mk, (catMap.get(cat).get(mk) ?? 0) + signed);
        const subLabel = r.subcategory ? `${r.category}: ${r.subcategory}` : `${r.category}: (Uncategorized)`;
        if (!subMap.has(subLabel)) subMap.set(subLabel, new Map());
        subMap.get(subLabel).set(mk, (subMap.get(subLabel).get(mk) ?? 0) + signed);
      }
      function listFrom(map) {
        const rows = [];
        for (const [label, perKey] of map.entries()) {
          const values = periodKeys.map((k) => perKey.get(k) ?? 0);
          const slope = trendFor(values);
          if (Math.abs(slope) < 1e-9) continue;
          rows.push({ label, slope });
        }
        const upAll = rows.filter(r => r.slope > 0).sort((a,b) => b.slope - a.slope);
        const downAll = rows.filter(r => r.slope < 0).sort((a,b) => a.slope - b.slope);
        return { upTop: upAll.slice(0,3), downTop: downAll.slice(0,3) };
      }
      const cats = listFrom(catMap);
      const subs = listFrom(subMap);
      setMovers({
        catsImprovingTop: cats.upTop,
        catsWorseningTop: cats.downTop,
        subsImprovingTop: subs.upTop,
        subsWorseningTop: subs.downTop
      });

      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
        setBooting(false); 
      }
    });
    return () => unsub();
  }, []);

  // Watch Plaid connection status for this user and surface ITEM_LOGIN_REQUIRED
  useEffect(() => {
    if (!user?.uid) return;
    const ref = doc(db, "users", user.uid, "plaid", "state");
    const unsub = onSnapshot(ref, (snap) => {
      const d = snap.exists() ? (snap.data() || {}) : {};
      // Mirror Transactions logic: show when either we have an error status OR ITEM_LOGIN_REQUIRED
      // Also honor the Firestore flag if you set needs_login_repair server-side.
      const broken =
        d?.errorCode === "ITEM_LOGIN_REQUIRED" ||
        d?.connectionStatus === "error" ||
        d?.needs_login_repair === true;
      setPlaidBroken(!!broken);
      setPlaidErrCode(d.errorCode || null);
    }, (err) => {
      console.warn("onSnapshot(plaid/state) failed:", err);
      setPlaidBroken(false);
      setPlaidErrCode(null);
    });
    return () => unsub();
  }, [user?.uid]);

  // Build chart series whenever rows change (robust even if initial load order shifts)
  useEffect(() => {
    // Guard: nothing to do until we have a user and some rows
    if (!user) return;

    try {
      // --- Last 3 full months series (Reports rules): line (net) + bar (stacked inc/exp)
      const { start: l3Start, end: l3End } = last3FullMonthsCombined();
      const rowsF = l3RowsState.filter(isBudgetRow);
      if (rowsF.length > 0) {
        // Aggregate by month
        const byMonth = new Map(); // key -> { incomeSigned, expenseMag, savingsMag }
        const incStacks = new Map(); // key -> Map(cat -> signed income)
        const expStacks = new Map(); // key -> Map(cat -> expense magnitude)
        const savStacks = new Map(); // key -> Map(cat -> savings magnitude)
        for (const r of rowsF) {
          const d = asDate(r.date); if (isNaN(d)) continue;
          const key = ymKey(new Date(d.getFullYear(), d.getMonth(), 1));
          const rec = byMonth.get(key) || { incomeSigned: 0, expenseMag: 0, savingsMag: 0 };
          if (isIncome(r)) {
            rec.incomeSigned += signedForNet(r); // includes negatives (reversals)
            const m = incStacks.get(key) || new Map();
            const lab = catLabelOf(r);
            m.set(lab, (m.get(lab) || 0) + Number(r.amount || 0));
            incStacks.set(key, m);
          } else if (isSavings(r)) {
            rec.savingsMag = addToExpenseMagnitude(rec.savingsMag, r.amount);
            const m = savStacks.get(key) || new Map();
            const lab = catLabelOf(r);
            const next = addToExpenseMagnitude(m.get(lab) || 0, r.amount);
            m.set(lab, next);
            savStacks.set(key, m);
          } else if (!isIncome(r)) {
            // regular expense
            rec.expenseMag = addToExpenseMagnitude(rec.expenseMag, r.amount);
            const m = expStacks.get(key) || new Map();
            const lab = catLabelOf(r);
            const next = addToExpenseMagnitude(m.get(lab) || 0, r.amount);
            m.set(lab, next);
            expStacks.set(key, m);
          }
          byMonth.set(key, rec);
        }
        const keys = monthKeysBetween(l3Start, l3End);
        // Bar series: stacked detail
        const stacked = keys.map(k => {
          const incMap = incStacks.get(k) || new Map();
          const expMap = expStacks.get(k) || new Map();
          const savMap = savStacks.get(k) || new Map();
          // sort segments by size desc
          const incomeSegments = [...incMap.entries()].sort((a,b)=>Math.abs(b[1])-Math.abs(a[1]))
            .map(([label,value]) => ({ label, value: Math.max(0, Number(value||0)) })); // up-only
          const expenseSegments = [...expMap.entries()].sort((a,b)=>Math.abs(b[1])-Math.abs(a[1]))
            .map(([label,value]) => ({ label, value: Math.max(0, Number(value||0)) })); // down-only
          const savingsSegments = [...savMap.entries()].sort((a,b)=>Math.abs(b[1])-Math.abs(a[1]))
           .map(([label,value]) => ({ label, value: Math.max(0, Number(value||0)) })); // down
          const income = Number(byMonth.get(k)?.incomeSigned || 0);
          const expense = Number(byMonth.get(k)?.expenseMag || 0);
          const savings = Number(byMonth.get(k)?.savingsMag || 0);
          return {
            label: k,
            incomeSegments,
            expenseSegments,
            savingsSegments,
            totals: { income, expense, savings, net: income - (expense + savings) },
          };
        });
        console.groupCollapsed("[DBG:L3:STACKED] keys & totals");
        console.log(stacked.map(d => ({ label: d.label, ...d.totals })));
        console.groupEnd();
        setSeriesL3IE(stacked);
        // Line series: net per month using Reports net math
        setSeriesL3Net(stacked.map(d => ({ label: d.label, net: d.totals.net })));
      } else {
        setSeriesL3IE([]); setSeriesL3Net([]);
      }
    } catch (err) {
      console.error("Series build failed:", err);
      setSeriesL3IE([]); setSeriesL3Net([]);
    }

    // High-level trend badge (last 3 full months net)
    {
      const vals = (seriesL3Net || []).map(d => Number(d.net || 0));
      const slope = trendFor(vals); // you already have trendFor above
      setTrendBadge(slope > 1e-6 ? "up" : slope < -1e-6 ? "down" : "flat");
    }

    // --- Current month series: pies (income by category, expense+savings by category)
    const cmF = cmRowsState.filter(isBudgetRow);
    if (cmF.length > 0) {
      const incByCat = new Map();   // signed income
      const expByCat = new Map();   // outflow magnitude (refunds reduce)
      const savByCat = new Map();   // savings magnitude
      for (const r of cmF) {
        const key = r.category || "(Uncategorized)";
        if (isIncome(r)) {
          incByCat.set(key, (incByCat.get(key) || 0) + Number(r.amount || 0)); // signed
        } else if (r._type === "expense") {
          const cur = expByCat.get(key) || 0;
          expByCat.set(key, addToExpenseMagnitude(cur, r.amount));
        } else if (isSavings(r)) {
          const cur = savByCat.get(key) || 0;
          savByCat.set(key, addToExpenseMagnitude(cur, r.amount));
        }
      }
      function top5PlusOther(map) {
        const arr = [...map.entries()].sort((a,b) => b[1] - a[1]);
        const top = arr.slice(0, 5);
        const topSum = top.reduce((s, [,v]) => s + v, 0);
        const total = arr.reduce((s, [,v]) => s + v, 0);
        const other = Math.max(0, total - topSum);
        // clamp negatives to 0 for slices
        const out = top.map(([label, value]) => ({ label, value: Math.max(0, value) }));
        if (other > 0) out.push({ label: "Other", value: other });
        return out;
      }
      // Build current-month pies using locals so we can log sizes first
      const incPie = top5PlusOther(incByCat);
      const expPie = top5PlusOther(expByCat);
      const savPie = top5PlusOther(savByCat);

      console.groupCollapsed("[DBG:PIES:CM]");
      console.table({
        incSlices: incPie.length,
        expSlices: expPie.length,
        savSlices: savPie.length
      });
      console.groupEnd();

      setPieIncomeCM(incPie);
      setPieExpenseCM(expPie);
      setPieSavingsCM(savPie);
    }
  }, [user, cmRowsState, l3RowsState]);

  // (debug) showRuleModal tracing is handled near the Needs Action state section via traceNA

  // derived display-only splits for current month (keep cmTotals.net as-is)
  const cmExpenseOnly = cmRowsState.filter(isBudgetRow).reduce((acc, r) => {
    return r._type === "expense" ? addToExpenseMagnitude(acc, r.amount) : acc;
  }, 0);
  const cmSavingsOnly = cmRowsState.filter(isBudgetRow).reduce((acc, r) => {
    return r._type === "savings" ? addToExpenseMagnitude(acc, r.amount) : acc;
  }, 0);

  // derived splits for last 3 months from the stacked series (keeps Net unchanged)
  const l3IncomeSum  = seriesL3IE.reduce((s,d)=>s + (d?.totals?.income||0), 0);
  const l3ExpenseSum = seriesL3IE.reduce((s,d)=>s + (d?.totals?.expense||0), 0);
  const l3SavingsSum = seriesL3IE.reduce((s,d)=>s + (d?.totals?.savings||0), 0);
  const l3NetSum     = seriesL3IE.reduce((s,d)=>s + (d?.totals?.net||0), 0);

  return (
  <ErrorCatcher>
    <div
      className="trelli-section dash-grid trelli-no-vw"
      style={{
        marginLeft: isMobile ? 0 : 5,
        boxSizing: "border-box",
        overflowX: "hidden",
        maxWidth: "100%"
      }}
    >
      {applyingRule && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.25)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999
          }}
        >
          <div
            style={{
              background: "#fff",
              border: "2px solid #00B074",
              borderRadius: 12,
              padding: "18px 22px",
              boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
              minWidth: 280,
              textAlign: "center"
            }}
          >
            <div style={{ fontWeight: 800, marginBottom: 4, color: "#00B074" }}>
              Applying rule…
            </div>
            <div style={{ fontSize: 13, color: "#333" }}>
              Checking & updating {applyingRuleCount} matching transaction{applyingRuleCount === 1 ? "" : "s"}
            </div>
          </div>
        </div>
      )}

      <h1 className="dash-title">
        <span className="dash-title-desktop">Welcome to your Dashboard</span>
        <span className="dash-title-mobile">Dashboard</span>
      </h1>

      {/* mobile-only heading CSS */}
      <style>
      {`
        @media (max-width: 768px) {
          .dash-title { 
            text-align: center;      /* ✅ center the header text on phones */
            margin: 0 0 0.75rem 0;   /* tidy spacing below */
          }
          .dash-title-desktop { display: none; }
        }
        @media (min-width: 769px) {
          .dash-title-mobile { display: none; }
        }
      `}
      </style>

      <style>
      {`
        /* Mobile-only uniform spacing between stacked cards */
        @media (max-width: 768px) {
          .dash-card { margin-bottom: 1.25rem; }
          .dash-card:last-child { margin-bottom: 0; }
        }
      `}
      </style>

      {/* mobile layout helpers */}
      <style>
      {`
        @media (max-width: 768px) {
          .dash-grid { 
            display: block;        /* 1 column on phones */
          }
          .dash-card {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            grid-column: 1 / -1 !important;
            margin: 0 auto 1.25rem auto;     /* ✅ uniform spacing under each section + centered */
            box-sizing: border-box;
          }
          /* If any row wrapper exists, make sure it can shrink */
          .dash-row { min-width: 0 !important; }
        }
      `}
      </style>

      <style>
      {`
        @media (max-width: 768px) {
          html, body, #root { overflow-x: hidden; }
          img, svg { max-width: 100%; height: auto; } /* defensive, keeps visuals from forcing overflow */
        }
      `}
      </style>

      <style>
      {`
        @media (min-width: 769px) {
          .mobile-only { display: none !important; }
        }
      `}
      </style>

      {!accountsConnected && (
        <div
          className="dash-card"
          style={{
            backgroundColor: "#F4C542",
            border: "1px solid #E7BD3C",
            borderRadius: 8,
            padding: isMobile ? "12px" : "1rem",
            margin: "1rem 0",
            color: "#222",
            maxWidth: "100%",
            boxSizing: "border-box",
            overflowX: "hidden"
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "stretch" : "center",
              justifyContent: "space-between",
              gap: isMobile ? 8 : 12
            }}
          >
            {/* Title / copy */}
            <div style={{ fontWeight: 800 }}>
              {isMobile ? "Connect a bank account" : "Connect your accounts"}
            </div>

            {/* Desktop-only helper text (hidden on mobile) */}
            {!isMobile && (
              <div style={{ flex: "1 1 auto", color: "#222" }}>
                Link a bank for accurate tracking of your spending and income.
              </div>
            )}

            {/* Button (full width on mobile) */}
            <button
              onClick={() => {
                try { localStorage.setItem("trelli_settings_tab", "plaid"); } catch {}
                navigate("/settings?tab=plaid");
              }}
              style={{
                alignSelf: isMobile ? "stretch" : "auto",
                width: isMobile ? "100%" : "auto",
                marginTop: isMobile ? 4 : 0,
                background: "#00B074",
                color: "#fff",
                border: "none",
                borderRadius: 8,
                padding: "0.5rem 0.75rem",
                fontWeight: 700,
                cursor: "pointer",
                textAlign: "center"
              }}
            >
              Link accounts
            </button>
          </div>
        </div>
      )}

      {plaidBroken && (
        <div
          style={{
            border: "1px solid #ffe69c",
            background: "#fff8e1",
            color: "#664d03",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "16px"
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 6 }}>
            ⚠️ Bank connection needs attention
          </div>
          <div style={{ marginBottom: 12 }}>
            We’re not receiving new transactions because your bank needs you to re-authenticate.
          </div>
          <button
            type="button"
            onClick={() => {
              try { localStorage.setItem("trelli_settings_tab", "plaid"); } catch {}
              // Switch the App's internal view now (no flash)
              window.dispatchEvent(new CustomEvent("trelli:navigate", { detail: "settings" }));
              // Also update the URL so your route watcher stays truthful
              navigate("/settings?tab=plaid");
            }}
            style={{ background: "#00B074", color: "#fff", border: "none", padding: "0.5rem 0.8rem", borderRadius: 6 }}
          >
            Fix Connection
          </button>
        </div>
      )}

      {/* ───────────────────────────── Needs Action (Dashboard) ───────────────────────────── */}
      {(uncategorizedTransactions.length > 0) && (
        <div
          className="dash-card"
          style={{
            border: "2px solid #F4C542",
            padding: "0.5rem 0.75rem",
            borderRadius: "8px",
            backgroundColor: "#fff9e6",
            marginBottom: "1rem",
            maxWidth: "100%",
            overflowX: "hidden",
            boxSizing: "border-box"
          }}
        >
          <h2
            style={{
              backgroundColor: "#F4C542",
              color: "#222",
              padding: "0.5rem 1rem",
              textAlign: "center",
              borderRadius: "8px",
              fontSize: "1.5rem"
            }}
          >
            Needs Action
          </h2>

          {needsActionInlineError && (
            <div
              style={{
                margin: "0.6rem 0 0.25rem",
                padding: "0.6rem 0.75rem",
                borderRadius: 8,
                border: "1px solid #c62828",
                background: "#fff",
                color: "#c62828",
                fontWeight: 700,
              }}
            >
              {needsActionInlineError}
            </div>
          )}

          {/* MOBILE condensed list — Vendor | Amount | Subcategory | Actions */}
          {isMobile && (
            <>
              {(() => {
                // Shared columns: Vendor | Amount | Subcategory | Actions
                const COLS = "minmax(0,1fr) 72px minmax(0,1fr) 56px";
                const headerRowStyle = {
                  display: "grid",
                  gridTemplateColumns: COLS,
                  gap: 6,
                  padding: "6px 10px",
                  borderBottom: "1px solid #e6d38f",
                  fontSize: 12,
                  color: "#666",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                };
                const rowStyle = {
                  display: "grid",
                  gridTemplateColumns: COLS,
                  gap: 6,
                  alignItems: "center",
                  padding: "10px 10px",
                  borderBottom: "1px solid #e6d38f",
                  background: "#fff9e6",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                };

                // Buttons
                const btnBase = {
                  width: 26,
                  height: 26,
                  borderRadius: 6,
                  display: "grid",
                  placeItems: "center",
                  padding: 0,
                  cursor: "pointer",
                  lineHeight: 1,
                };
                const btnConfirm = {
                  ...btnBase,
                  background: TRELLI_GREEN,
                  border: "1px solid #009363",
                  color: "#fff",
                  fontWeight: 800,
                };
                const btnEllipsis = {
                  ...btnBase,
                  background: "#fff",
                  border: "1px solid #d1d5db",
                  color: "#111",
                  fontWeight: 800,
                };

                // Be defensive: read true total from uncategorizedTransactions
                // (Define outside try so nested IIFEs/handlers can always see it)
                const totalNeedsAction = (uncategorizedTransactions || []).length;

                try {
                  const list = (needsActionWithSuggestions || []).slice(
                    0,
                    Math.min(needsMobileCount, totalNeedsAction || 0)
                  );

                  return (
                    <>
                      {/* Header */}
                      <div style={headerRowStyle}>
                        <div>Vendor</div>
                        <div style={{ textAlign: "right" }}>Amount</div>
                        <div>Subcategory</div>
                        <div style={{ textAlign: "center" }}>Actions</div>
                      </div>

                      {/* Rows */}
                      {list.map((tx) => {
                        const suggested = {
                          category: tx.suggestedCategory || "",
                          subcategory: tx.suggestedSubcategory || "",
                        };

                        // build subcategory label (suggested first, then matched), clamp to avoid overlap
                        const subFull = String(
                          suggested.subcategory || tx.matchedSubcategory || ""
                        ).trim();
                        const subDisplay =
                          subFull.length > 22 ? subFull.slice(0, 21) + "…" : subFull;

                        return (
                          <React.Fragment key={tx.id}>
                            <div style={rowStyle}>
                              {/* Vendor */}
                              <div
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  minWidth: 0,
                                }}
                                title={tx.vendor || ""}
                              >
                                {tx.vendor || "(Unknown)"}
                              </div>

                              {/* Amount */}
                              <div
                                style={{
                                  textAlign: "right",
                                  fontWeight: 700,
                                  fontSize: 13,
                                  color: tx.amount < 0 ? TRELLI_RED : TRELLI_GREEN,
                                }}
                              >
                                {toUSD(tx.amount)}
                              </div>

                              {/* Subcategory */}
                              <div
                                title={subFull}
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  minWidth: 0,
                                  fontSize: 12,
                                  color: "#333",
                                }}
                              >
                                {subDisplay || <span style={{ color: "#999" }}>(choose subcategory)</span>}
                              </div>

                              {/* Actions */}
                              <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                                <button
                                  aria-label="Confirm"
                                  title="Confirm"
                                  onClick={() => handleConfirmWithoutRule(buildConfirmModalTx(tx, suggested))}
                                  style={btnConfirm}
                                >
                                  ✓
                                </button>
                                <button
                                  aria-label="More"
                                  title="More"
                                  onClick={() => setNeedsActionMobileMenuTx(buildConfirmModalTx(tx, suggested))}
                                  style={btnEllipsis}
                                >
                                  <span style={{ transform: "translateY(1px)" }}>…</span>
                                </button>
                              </div>
                            </div>

                            {needsActionMobileExpandedId === tx.id && (
                              <div
                                style={{
                                  padding: "8px 10px 12px",
                                  borderBottom: "1px solid #e6d38f",
                                  background: "#fff9e6",
                                  fontSize: 12,
                                  color: "#222",
                                  lineHeight: 1.45,
                                }}
                              >
                                <div><b>Date:</b> {formatDate(tx.date)}</div>

                                <div style={{ marginTop: 4 }}>
                                  <b>Account:</b>{" "}
                                  {accountNameById?.[tx.accountId] ||
                                    accountNameById?.[tx.account_id] ||
                                    tx.accountName ||
                                    tx.account_name ||
                                    tx.accountId ||
                                    tx.account_id ||
                                    "—"}
                                </div>

                                <div style={{ marginTop: 4 }}>
                                  <b>Bank category:</b>{" "}
                                  {Array.isArray(tx.category)
                                    ? (tx.category[0] || "—")
                                    : (tx.bankCategory || tx.plaidCategory || "—")}
                                </div>

                                {tx.memo ? (
                                  <div style={{ marginTop: 4 }}>
                                    <b>Memo:</b> {tx.memo}
                                  </div>
                                ) : null}
                              </div>
                            )}
                          </React.Fragment>
                        );
                      })}

                      {/* Footer controls */}
                      {(() => {
                        if ((totalNeedsAction || 0) <= 5) return null;
                        return (
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              justifyContent: "center",
                              alignItems: "center",
                              padding: "12px 10px",
                              background: "#fff9e6",
                              borderTop: "1px solid #e6d38f",
                            }}
                          >
                            <button
                              type="button"
                              onClick={goToNeedsAction}
                              onTouchEnd={(e) => {
                                e.preventDefault();
                                goToNeedsAction();
                              }}
                              style={{
                                background: "#00B074",
                                color: "#fff",
                                border: "1px solid #009363",
                                borderRadius: 8,
                                padding: "8px 12px",
                                fontWeight: 700,
                                cursor: "pointer",
                                minWidth: 0,
                              }}
                            >
                              View all ({totalNeedsAction || 0})
                            </button>
                          </div>
                        );
                      })()}
                    </>
                  );
                } catch (e) {
                  console.error("[NeedsAction:mobile] render error:", e);
                  return (
                    <div style={{ padding: "10px 8px", color: "#B00020", fontSize: 12 }}>
                      Something went wrong rendering this list. Please refresh.
                    </div>
                  );
                }
              })()}
            </>
          )}

          {!isMobile && (
          <>
          {/* Column width map (header + rows share) */}
          {(() => {
            const NEEDS_ACTION_COL_STYLES = {
              1: { flex: "2 0 180px", minWidth: 200, textAlign: "left",  paddingLeft: 8 }, // Vendor
              2: { flex: "1 0 110px", minWidth: 110, textAlign: "right" },                // Amount
              3: { flex: "0 0 0px",   minWidth: 0,   padding: 0, margin: 0 },             // Spacer
              4: { flex: "0 0 110px", minWidth: 110, textAlign: "center" },               // Date
              5: { flex: "0 0 150px", minWidth: 150, textAlign: "center" },               // Category
              6: { flex: "0 0 170px", minWidth: 170, textAlign: "center" },               // Subcategory
              7: { flex: "1 0 160px", minWidth: 160, textAlign: "left",  paddingLeft: 8 },// Memo
              8: { flex: "0 0 100px", minWidth: 100, textAlign: "center" },               // Status
              9: { flex: "0 0 120px", minWidth: 120, textAlign: "left" }                  // Actions
            };

            return (
              <>
                <div
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 25,
                    background: "#fff9e6",
                    borderBottom: "1px solid #ddd",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                    padding: "0.25rem 0.5rem",
                    fontWeight: 600,
                    color: "#444"
                  }}
                >
                  <div style={NEEDS_ACTION_COL_STYLES[1]}>Vendor</div>
                  <div style={NEEDS_ACTION_COL_STYLES[2]}>Amount</div>
                  <div style={NEEDS_ACTION_COL_STYLES[3]} />
                  <div style={NEEDS_ACTION_COL_STYLES[4]}>Date</div>
                  <div style={NEEDS_ACTION_COL_STYLES[5]}>Category</div>
                  <div style={NEEDS_ACTION_COL_STYLES[6]}>Subcategory</div>
                  <div style={NEEDS_ACTION_COL_STYLES[7]}>Memo</div>
                  <div style={NEEDS_ACTION_COL_STYLES[8]}>Status</div>
                  <div style={NEEDS_ACTION_COL_STYLES[9]}>Actions</div>
                </div>

                {(() => { window.__NA_COLS__ = NEEDS_ACTION_COL_STYLES; return null; })()}
              </>
            );
          })()}

          {uncategorizedTransactions.length === 0 && (
            <p style={{ textAlign: "center", color: "#666" }}>All caught up! No transactions need action.</p>
          )}

          {(showAllNeedsAction ? needsActionWithSuggestions : needsActionWithSuggestions.slice(0, 5)).map((tx, idx) => {
            // use the precomputed suggestions from needsActionWithSuggestions
            const suggested = {
              category: tx.suggestedCategory || "",
              subcategory: tx.suggestedSubcategory || "",
            };

            // Start with whatever Firestore/suggestions say...
            const baseCategory = tx.matchedCategory || suggested.category || "";
            const baseSubcategory = tx.matchedSubcategory || suggested.subcategory || "";

            // ...but allow the user to stage edits locally (do not write to Firestore yet)
            const localEdit = getNeedsActionEdit(tx.id);
            const rawCategory = localEdit?.category ?? baseCategory;
            const rawSubcategory = localEdit?.subcategory ?? baseSubcategory;

            const categoryForUI = String(rawCategory || "");
            const subcategorySanitized = sanitizeNeedsActionSubcategory(tx, rawSubcategory);

            // If subcategory doesn't belong under the selected category, treat as blank
            const subcategoryForUI = isSubValidUnderCategory(categoryForUI, subcategorySanitized)
              ? subcategorySanitized
              : "";

            const selectionValid = isActiveBudgetSelection(categoryForUI, subcategoryForUI);

            // Show confirm if either:
            // - it was previously suggestable (existing behavior), OR
            // - the user has staged a valid selection locally
            const showConfirmBase =
              (!tx.matchedSource && tx.suggestedCategory) ||
              tx.matchedSource === "suggested" ||
              tx.matchedSource === "pseudoRule" ||
              !!localEdit; // user touched it in this session

            const showConfirm = selectionValid && showConfirmBase;

            const guardConfirmOrRule = (actionLabel) => {
              if (selectionValid) return true;
              setNeedsActionInlineError(`Select a valid category and subcategory before you ${actionLabel}.`);
              return false;
            };

            if (DEBUG_NEEDS && idx < 25) {
              console.log("[NeedsAction] UI row", {
                id: tx.id,
                vendor: tx.vendor,
                out_category_prop: categoryForUI,
                out_subcategory_prop: subcategoryForUI,
                selectionValid,
                showConfirm,
              });
            }

            return (
            <TransactionRowStyled
              key={tx.id}
              tx={{
                id: tx.id,
                vendor: tx.vendor,
                amount: tx.amount,
                income: tx.amount > 0 ? tx.amount : null,
                expense: tx.amount < 0 ? tx.amount : null,

                // Use the helper already in Dashboard.jsx (handles ISO strings safely)
                date: formatDate(tx.date),

                // Prefill dropdowns (sanitized)
                category: categoryForUI,
                subcategory: subcategoryForUI,

                // ✅ Details panel fields (THIS is what fixes the white bar)
                accountName:
                  accountNameById?.[tx.accountId] ||
                  accountNameById?.[tx.account_id] ||
                  tx.accountName ||
                  tx.account_name ||
                  tx.accountId ||
                  tx.account_id ||
                  "",

                // Transactions page behavior: show Plaid category[0] when present
                bankCategory: Array.isArray(tx.category)
                  ? (tx.category[0] || "")
                  : (tx.bankCategory || tx.plaidCategory || ""),

                // Shows in the Memo column (and is useful elsewhere)
                memo: tx.memo || "",

                // TransactionRowStyled renders this as "Note:" in details
                // Put memo first (what you asked for), and keep reviewMessage if present
                reason: [tx.memo, tx.reviewMessage].filter(Boolean).join(" • "),

                // Status + confirm gating based on active budgets
                status: !selectionValid ? "🔴" : (showConfirm ? "🟡" : "🟢"),
                showConfirm: !!showConfirm,
              }}
              idx={idx}
              onMemoSave={(id, memo) => {}}

              // ✅ Desktop Confirm = confirm this transaction ONLY (no rule)
              onConfirm={async () => {
                if (!guardConfirmOrRule("confirm")) return;

                const edit = getNeedsActionEdit(tx.id);
                const finalCategory = edit?.category ?? categoryForUI;
                const finalSubcategory = edit?.subcategory ?? subcategoryForUI;

                // Clear local staging so it doesn't linger
                clearNeedsActionEdit(tx.id);

                // Confirm using the selected values (handleConfirmWithoutRule now honors matched*)
                const built = buildConfirmModalTx(tx, suggested);
                built.matchedCategory = finalCategory;
                built.matchedSubcategory = finalSubcategory;

                await handleConfirmWithoutRule(built);
              }}

              onOpenConfirmAndRule={() => {
                if (!guardConfirmOrRule("confirm and set a rule")) return;

                const edit = getNeedsActionEdit(tx.id);
                const finalCategory = edit?.category ?? categoryForUI;
                const finalSubcategory = edit?.subcategory ?? subcategoryForUI;

                const built = buildConfirmModalTx(tx, suggested);
                built.matchedCategory = finalCategory;
                built.matchedSubcategory = finalSubcategory;

                setShowConfirmModal(built);
              }}

              onOpenRuleEditor={() => {
                // Creating a rule should be allowed even if category/subcategory is blank.
                // The modal should hard-stop saving until the rule is complete.
                setNeedsActionInlineError(null);

                const built = buildConfirmModalTx(tx, suggested);

                console.log("[Dashboard][NeedsAction] onOpenRuleEditor fired", {
                  id: built?.id,
                  vendor: built?.vendor,
                  categoryForUI,
                  subcategoryForUI,
                  selectionValid,
                });

                // close anything that could be on top
                setShowConfirmModal(null);
                setShowReassignModal(null);
                setShowDeleteTx(null);

                // IMPORTANT: Use the UI values (sanitized), not raw tx values
                setShowRuleModal({
                  tx: built,
                  initialCategory: categoryForUI || "",
                  initialSubcategory: subcategoryForUI || "",
                });
              }}

              onOpenReassign={() => {
                const payload = {
                  ...buildConfirmModalTx(tx, suggested),
                  matchedCategory: categoryForUI,
                  matchedSubcategory: subcategoryForUI,
                };

                console.log("[Dashboard][NeedsAction] onOpenReassign fired", {
                  id: payload.id,
                  vendor: payload.vendor,
                  matchedCategory: payload.matchedCategory,
                  matchedSubcategory: payload.matchedSubcategory,
                });

                // close other modals just in case
                setShowConfirmModal(null);
                setShowRuleModal(null);
                setShowDeleteTx(null);

                setShowReassignModal(payload);
              }}

              // ✅ Pass the same budgets array used elsewhere (not a transformed copy)
              budgets={dropdownBudgets}

              // keep your columnStyles prop as-is if you have one
              columnStyles={[
              {}, // index 0 unused
              window.__NA_COLS__?.[1] ?? {},
              window.__NA_COLS__?.[2] ?? {},
              window.__NA_COLS__?.[3] ?? {},
              window.__NA_COLS__?.[4] ?? {},
              window.__NA_COLS__?.[5] ?? {},
              window.__NA_COLS__?.[6] ?? {},
              window.__NA_COLS__?.[7] ?? {},
              window.__NA_COLS__?.[8] ?? {},
              window.__NA_COLS__?.[9] ?? {},
            ]}


              // ✅ Write-back handler like BudgetBuilderManual (adjust to your existing updater if named differently)
              onCategoryChange={(id, value, field) => {
                // Do NOT write to Firestore here.
                // Stage locally; user must explicitly Confirm (or save rule) to persist.
                setNeedsActionInlineError(null);

                const nextValue = String(value || "");

                if (field === "category") {
                  const nextCategory = nextValue;

                  // Reset subcategory when category changes (unless still valid)
                  const currentSub = getNeedsActionEdit(id)?.subcategory ?? subcategoryForUI;
                  const keepSub = isSubValidUnderCategory(nextCategory, currentSub);
                  const nextSub = keepSub ? currentSub : "";

                  setNeedsActionEdit(id, { category: nextCategory, subcategory: nextSub });
                  return;
                }

                if (field === "subcategory") {
                  const currentCat = getNeedsActionEdit(id)?.category ?? categoryForUI;
                  if (!currentCat) {
                    setNeedsActionInlineError("Select a category first.");
                    return;
                  }

                  setNeedsActionEdit(id, { subcategory: nextValue });
                  return;
                }

                console.warn("[NeedsAction] Unknown field passed to onCategoryChange:", field);
              }}



              // ✅ Desktop "More options" menu
              onEdit={() => {
                const built = buildConfirmModalTx(tx, suggested);
                traceNA("Kebab clicked (desktop path)", {
                  isMobile,
                  builtTruthy: !!built,
                  builtId: built?.id,
                  builtVendor: built?.vendor,
                  rawId: tx?.id,
                  rawVendor: tx?.vendor,
                });
                setNeedsActionMenuTx(built);
              }}
              editLabel="⋯"

              onDelete={() => setShowDeleteTx(buildConfirmModalTx(tx, suggested))}

              // keep other props you already pass (rowGap, compactSelects, etc.)
              singleAmount={true}
              rowGap="0.25rem"
              compactSelects={true}
            />
          );
        })}

          {uncategorizedTransactions.length > 5 && (
            <div
              style={{
                position: "sticky",
                bottom: 0,
                zIndex: 26,
                background: "#fff9e6",
                padding: "0.5rem 0.75rem",
                borderTop: "1px solid #e6d38f",
                display: "flex",
                justifyContent: "center",
                gap: "1rem",
                boxShadow: "0 -2px 6px rgba(0,0,0,0.06)"
              }}
            >
              {!showAllNeedsAction ? (
                <button
                  type="button"
                  onClick={goToNeedsAction}
                  style={{
                    color: "#00B074",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: "0.95rem"
                  }}
                >
                  {`View all (${needsCountAll || 0}) transactions that need action`}
                </button>
              ) : (
                <button
                  onClick={() => setShowAllNeedsAction(false)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#00B074",
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: "0.95rem"
                  }}
                >
                  Collapse all transactions that need attention
                </button>
              )}
            </div>
          )}
        </>
        )}
        {showConfirmModal && (
            <RuleConfirmationModal
              tx={showConfirmModal}
              onConfirm={() => handleConfirmAndSaveRule(showConfirmModal)}
              onConfirmSingle={() => handleConfirmWithoutRule(showConfirmModal)}
              onEdit={() => {
                setShowRuleModal({
                  tx: showConfirmModal,
                  initialCategory: showConfirmModal.matchedCategory,
                  initialSubcategory: showConfirmModal.matchedSubcategory
                });
                setShowConfirmModal(null);
              }}
              onCancel={() => setShowConfirmModal(null)}
            />
          )}

          {showRuleModal && !isMobile && console.log(
            "[Dashboard][Desktop] Rule modal portal render",
            {
              id: showRuleModal?.tx?.id,
              vendor: showRuleModal?.tx?.vendor,
            }
          )}

          {showRuleModal && (
            <RuleCreator
              tx={showRuleModal.tx}
              initialCategory={showRuleModal.initialCategory}
              initialSubcategory={showRuleModal.initialSubcategory}
              budgets={dropdownBudgets}
              accountNameById={accountNameById}
              onClose={() => setShowRuleModal(null)}
              onSave={handleSaveRule}
            />
          )}

          {isMobile && needsActionMobileMenuTx && createPortal(
            <div
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 20000,
                background: "rgba(0,0,0,0.15)",
              }}
              onClick={() => setNeedsActionMobileMenuTx(null)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  position: "absolute",
                  bottom: 12,
                  left: 12,
                  right: 12,
                  background: "#fff",
                  borderRadius: 12,
                  boxShadow: "0 12px 30px rgba(0,0,0,0.25)",
                  overflow: "hidden",
                }}
              >
                <div style={{ padding: "10px 12px", borderBottom: "1px solid #eee", fontWeight: 800 }}>
                  Actions
                </div>

                <button
                  onClick={() => {
                    const tx = needsActionMobileMenuTx;
                    setNeedsActionMobileMenuTx(null);

                    // close anything that could be on top
                    setShowRuleModal(null);
                    setShowReassignModal(null);
                    setShowDeleteTx(null);

                    setShowConfirmModal(tx);
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#111",
                  }}
                >
                  Confirm and set rule as suggested
                </button>

                <button
                  onClick={() => {
                    const tx = needsActionMobileMenuTx;
                    setNeedsActionMobileMenuTx(null);
                    setShowRuleModal({
                      tx,
                      initialCategory: tx.matchedCategory || tx.suggestedCategory || "",
                      initialSubcategory: tx.matchedSubcategory || tx.suggestedSubcategory || "",
                    });
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#111",
                  }}
                >
                  Create a new rule
                </button>

                <button
                  onClick={() => {
                    const tx = needsActionMobileMenuTx;
                    setNeedsActionMobileMenuTx(null);
                    setShowReassignModal({
                      ...tx,
                      matchedCategory: tx.matchedCategory || tx.suggestedCategory || "",
                      matchedSubcategory: tx.matchedSubcategory || tx.suggestedSubcategory || "",
                    });
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#111",
                  }}
                >
                  Reassign transaction only without rule
                </button>

                <button
                  onClick={() => {
                    const tx = needsActionMobileMenuTx;
                    setNeedsActionMobileMenuTx(null);
                    setShowDeleteTx(tx);
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#c62828",
                    borderTop: "1px solid #eee",
                  }}
                >
                  Delete transaction
                </button>

                <button
                  onClick={() => {
                    const tx = needsActionMobileMenuTx;
                    setNeedsActionMobileMenuTx(null);
                    setNeedsActionMobileExpandedId((prev) => (prev === tx.id ? null : tx.id));
                  }}
                  style={{
                    width: "100%",
                    textAlign: "left",
                    padding: "12px",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#111",
                    borderTop: "1px solid #eee",
                    fontWeight: 700,
                  }}
                >
                  Transaction details
                </button>

              </div>
            </div>,
            document.body
          )}

          {/* (debug) moved to traceNA + useEffects to avoid render-spam */}

          {showReassignModal &&
            createPortal(
              <div
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(0,0,0,0.35)",
                  zIndex: 30001,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
                onClick={() => setShowReassignModal(null)}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    background: "#fff",
                    padding: "1rem",
                    borderRadius: 8,
                    width: "92%",
                    maxWidth: 420,
                    boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                  }}
                >
                  <h3 style={{ marginTop: 0, marginBottom: "0.5rem" }}>Reassign Transaction</h3>

                  <div style={{ fontSize: "0.9rem", marginBottom: "0.75rem", color: "#333" }}>
                    <div><strong>Vendor:</strong> {showReassignModal.vendor}</div>
                    <div><strong>Date:</strong> {formatDate(showReassignModal.date)}</div>
                    <div>
                      <strong>Amount:</strong>{" "}
                      {Number(showReassignModal.amount).toLocaleString(undefined, { style: "currency", currency: "USD" })}
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
                    <label>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>Category</div>
                      <select
                        value={showReassignModal.matchedCategory || ""}
                        onChange={(e) =>
                          setShowReassignModal((prev) => ({
                            ...prev,
                            matchedCategory: e.target.value,
                            matchedSubcategory: "",
                          }))
                        }
                        style={{
                          width: "100%",
                          height: 38,
                          borderRadius: 8,
                          border: "1px solid #ccc",
                          padding: "0 8px",
                        }}
                      >
                        <option value="">Select category</option>
                        {(dropdownBudgets || []).map((b, i) => {
                          const label = typeof b?.category === "string" ? b.category : toLabel(b?.category ?? "");
                          return (
                            <option key={i} value={label}>
                              {label}
                            </option>
                          );
                        })}
                      </select>
                    </label>

                    <label>
                      <div style={{ fontWeight: 700, marginBottom: 4 }}>Subcategory</div>
                      <select
                        value={showReassignModal.matchedSubcategory || ""}
                        onChange={(e) =>
                          setShowReassignModal((prev) => ({
                            ...prev,
                            matchedSubcategory: e.target.value,
                          }))
                        }
                        style={{
                          width: "100%",
                          height: 38,
                          borderRadius: 8,
                          border: "1px solid #ccc",
                          padding: "0 8px",
                        }}
                      >
                        <option value="">Select subcategory</option>
                        {(
                          (dropdownBudgets || []).find(
                            (b) => toLabel(b.category) === toLabel(showReassignModal.matchedCategory)
                          )?.subcategories || []
                        ).map((s, i) => {
                          const label = typeof s === "string" ? s : s?.name ?? "";
                          return (
                            <option key={i} value={label}>
                              {label}
                            </option>
                          );
                        })}
                      </select>
                    </label>
                  </div>

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
                    <button
                      onClick={() => setShowReassignModal(null)}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 10,
                        border: "1px solid #ccc",
                        background: "#f0f0f0",
                        color: "#111",
                        fontWeight: 700,
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      onClick={async () => {
                        if (!showReassignModal.matchedCategory || !showReassignModal.matchedSubcategory) return;
                        await updateTransactionCategory(
                          showReassignModal.id,
                          showReassignModal.matchedCategory,
                          showReassignModal.matchedSubcategory,
                          "manual"
                        );
                        setShowReassignModal(null);
                      }}
                      style={{
                        padding: "8px 12px",
                        borderRadius: 10,
                        border: "none",
                        background: "#00B074",
                        color: "#fff",
                        fontWeight: 800,
                      }}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>,
              document.body
            )}

          {showDeleteTx && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.35)",
                zIndex: 20002,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={() => setShowDeleteTx(null)}
            >
              <div
                onClick={(e) => e.stopPropagation()}
                style={{
                  background: "#fff",
                  padding: "1rem",
                  borderRadius: 10,
                  width: "92%",
                  maxWidth: 420,
                  boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                }}
              >
                <div style={{ fontWeight: 900, fontSize: 16, marginBottom: 8 }}>Delete this transaction?</div>
                <div style={{ fontSize: 13, color: "#444", marginBottom: 12 }}>
                  This cannot be undone.
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  <button
                    onClick={() => setShowDeleteTx(null)}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 10,
                      border: "1px solid #ccc",
                      background: "#f0f0f0",
                      color: "#111",
                      fontWeight: 700,
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    onClick={async () => {
                      const id = showDeleteTx?.id;
                      setShowDeleteTx(null);
                      await deleteTransactionById(id);
                    }}
                    style={{
                      padding: "8px 12px",
                      borderRadius: 10,
                      border: "none",
                      background: "#c62828",
                      color: "#fff",
                      fontWeight: 800,
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      )}

      {/* DESKTOP Needs Action ⋯ menu */}
      {!isMobile && needsActionMenuTx && createPortal(
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 20000,
            background: "rgba(0,0,0,0.15)",
          }}
          onClick={() => setNeedsActionMenuTx(null)}
        >
          {DEBUG_ACTIONS && traceNA("Desktop menu rendered", {
            hasTx: !!needsActionMenuTx,
            txId: needsActionMenuTx?.id,
            vendor: needsActionMenuTx?.vendor,
          })}
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseDownCapture={(e) => {
              if (!DEBUG_ACTIONS) return;
              traceNA("Desktop menu mousedown CAPTURE", {
                targetTag: e.target?.tagName,
                text: (e.target?.innerText || "").slice(0, 80),
              });
            }}
            onClickCapture={(e) => {
              if (!DEBUG_ACTIONS) return;
              traceNA("Desktop menu click CAPTURE", {
                targetTag: e.target?.tagName,
                text: (e.target?.innerText || "").slice(0, 80),
              });
            }}
            style={{
              position: "absolute",
              top: 80,
              right: 24,
              width: 320,
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
              overflow: "hidden",
            }}
          >
            <div style={{ padding: "10px 12px", borderBottom: "1px solid #eee", fontWeight: 800 }}>
              Actions
            </div>

            <button
              onClick={() => {
                const tx = needsActionMenuTx;
                setNeedsActionMenuTx(null);
                setShowConfirmModal(tx);
              }}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "12px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#111",
              }}
            >
              Confirm and set rule as suggested
            </button>

            <div style={{ height: 1, background: "#eee" }} />

            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();

                const tx = needsActionMenuTx;

                traceNA("Create rule MOUSEDOWN", {
                  exists: !!tx,
                  id: tx?.id,
                  vendor: tx?.vendor,
                });

                if (!tx) return;

                // Open modal first
                setShowRuleModal({
                  tx,
                  initialCategory: tx.matchedCategory || tx.suggestedCategory || "",
                  initialSubcategory: tx.matchedSubcategory || tx.suggestedSubcategory || "",
                });

                // Then close the kebab menu (next tick to avoid unmount timing issues)
                setTimeout(() => {
                  traceNA("Close kebab after opening rule modal", { id: tx?.id });
                  setNeedsActionMenuTx(null);
                }, 0);
              }}
              onClick={(e) => {
                // If onClick fires, fine — but we don't depend on it.
                e.preventDefault();
                e.stopPropagation();
                traceNA("Create rule CLICK (secondary)", { id: needsActionMenuTx?.id });
              }}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "12px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#111",
              }}
            >
              Create a new rule
            </button>

            <div style={{ height: 1, background: "#eee" }} />

            {/* Reassign transaction only (no rule) */}
            <button
              style={{
                width: "100%",
                textAlign: "left",
                padding: "10px 12px",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();

                traceNA("Reassign-only MOUSEDOWN", {
                  menuTxExists: !!needsActionMenuTx,
                  id: needsActionMenuTx?.id,
                  vendor: needsActionMenuTx?.vendor,
                });

                // Open modal first
                setShowReassignModal(needsActionMenuTx);

                // Then close the kebab menu (next tick to avoid unmount timing issues)
                setTimeout(() => {
                  traceNA("Reassign-only: closing kebab (next tick)", { id: needsActionMenuTx?.id });
                  setNeedsActionMenuTx(null);
                }, 0);
              }}
              onClick={(e) => {
                // If onClick ever fires, log it (on desktop it may never happen)
                e.preventDefault();
                e.stopPropagation();
                traceNA("Reassign-only CLICK (secondary)", { id: needsActionMenuTx?.id });
              }}
            >
              Reassign transaction only (no rule)
            </button>

            <div style={{ height: 1, background: "#eee" }} />

            <button
              onClick={() => {
                const tx = needsActionMenuTx;
                setNeedsActionMenuTx(null);
                setShowDeleteTx(tx);
              }}
              style={{
                width: "100%",
                textAlign: "left",
                padding: "12px",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "#c62828",
              }}
            >
              Delete transaction
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Delete confirmation (Dashboard Needs Action) */}
      {showDeleteModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 21000,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          onClick={() => setShowDeleteModal(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 420,
              background: "#fff",
              borderRadius: 12,
              padding: 16,
              boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            }}
          >
            <div style={{ fontWeight: 800, marginBottom: 8 }}>Delete transaction?</div>
            <div style={{ fontSize: 13, color: "#333", marginBottom: 12 }}>
              This can’t be undone.
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button
                onClick={() => setShowDeleteModal(null)}
                style={{
                  border: "1px solid #ddd",
                  background: "#f7f7f7",
                  color: "#111",
                  borderRadius: 10,
                  padding: "10px 12px",
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const id = showDeleteModal?.id;
                  if (!id) return;
                  await deleteDoc(doc(db, "transactions", id));
                  setAllTxState((prev) =>
                    Array.isArray(prev) ? prev.filter((t) => t.id !== id) : prev
                  );
                  setShowDeleteModal(null);
                }}
                style={{
                  border: "1px solid #b71c1c",
                  background: "#d9534f",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "10px 12px",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <BootOverlay show={booting || loading || applyingRule} />

      {/* Month-End Projections */}
      {isMobile ? (
        <ProjectionsMobileCard
          projected={projectedNet}
          loading={loading}
          showInfo={showProjInfo}
          onToggleInfo={() => setShowProjInfo(v => !v)}
        />
      ) : (
        /* DESKTOP: keep your existing card exactly as-is */
        <div
          className="dash-card"
          style={{
            ...cardStyle,
            gridColumn: "1 / -1",
            marginBottom: "1rem",
            padding: "1.25rem",
            maxWidth: "100%",
            overflowX: "hidden"
          }}
        >
          <style>{`
            /* projections grid collapses to single column on mobile */
            @media (max-width: 768px) {
              .proj-grid {
                grid-template-columns: 1fr !important;
                width: 100%;
                box-sizing: border-box;
              }
              .proj-item  { display: flex; align-items: baseline; justify-content: space-between; }
              .proj-item small { font-size: 11px; color: #666; }
            }
          `}</style>

          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
            minWidth: 0
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <div style={{ fontSize: 14, color: "#666" }}>Month-End Projections</div>

              {/* info icon toggles the description text */}
              <button
                aria-label="Projection details"
                onClick={() => setShowProjInfo(v => !v)}
                style={{
                  width: 22, height: 22, borderRadius: 999, border: `1px solid ${TRELLI_GOLD}`,
                  background: "#FFFAE8", color: "#7A5A00", display: "grid", placeItems: "center",
                  cursor: "pointer", padding: 0, lineHeight: 1
                }}
                title="About projections"
              >
                i
              </button>
            </div>
            <TimeBadge basis="current" />
          </div>

          <div className="proj-grid" style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, minmax(160px, 1fr))",
            gap: 12,
            marginTop: 10
          }}>
            {/* Income */}
            <div className="proj-item" style={{ border: "1px solid #e5e5e5", borderRadius: 10, padding: "10px 12px" }}>
              <div style={{
                fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600,
                flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"
              }}>
                Projected Income
              </div>
              <div style={{ fontWeight: 800, color: TRELLI_GREEN, fontSize: 18, flex: "0 0 auto" }}>
                {toUSD(projectedNet.inc)}
              </div>
            </div>
            {/* Expenses */}
            <div className="proj-item" style={{ border: "1px solid #e5e5e5", borderRadius: 10, padding: "10px 12px" }}>
              <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Projected Expenses</div>
              <div style={{ fontWeight: 800, color: TRELLI_RED, fontSize: 18 }}>{toUSD(projectedNet.exp)}</div>
            </div>
            {/* Savings/Goals */}
            <div className="proj-item" style={{ border: "1px solid #e5e5e5", borderRadius: 10, padding: "10px 12px" }}>
              <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Projected Savings/Goals</div>
              <div style={{ fontWeight: 800, color: TRELLI_GOLD, fontSize: 18 }}>{toUSD(projectedNet.sav)}</div>
            </div>
            {/* Net + trend */}
            <div className="proj-item" style={{ border: "1px solid #e5e5e5", borderRadius: 10, padding: "10px 12px" }}>
              <div style={{ fontSize: 12, color: "#666", textTransform: "uppercase", fontWeight: 600 }}>Projected Net</div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ fontWeight: 800, color: "#111", fontSize: 18 }}>{toUSD(projectedNet.net)}</div>

                {/* Hide trend words on mobile */}
                {trendBadge === "up"   && <span style={{ fontSize: 12, color: TRELLI_GREEN }}>▲ improving</span>}
                {trendBadge === "down" && <span style={{ fontSize: 12, color: TRELLI_RED }}>▼ trending down</span>}
                {trendBadge === "flat" && <span style={{ fontSize: 12, color: "#666" }}>— flat</span>}
              </div>

              <div style={{ fontSize: 11, color: "#777", marginTop: 4 }}>
                Trend based on last 3 full months
              </div>
            </div>
          </div>

          {/* Projection disclaimer / alert (toggle by info “i” button) */}
          {showProjInfo && (
            <div
              style={{
                marginTop: 10,
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                background: "#FFFAE8",
                border: `1px solid ${TRELLI_GOLD}`,
                padding: "8px 10px",
                borderRadius: 8,
                color: "#7A5A00",
                fontSize: 12
              }}
            >
              <AlertIcon title="Projection notice" />
              <div>
                These numbers are projections based on your budgets and recent activity. 
                If some transactions need action, projections may shift as you confirm or edit them.
              </div>
            </div>
          )}
        </div>
      )}
      
      <div className="mobile-only">
        <NetSummaryMobileToggle
          cmTotals={cmTotals}
          cmExpenseOnly={cmExpenseOnly}
          cmSavingsOnly={cmSavingsOnly}
          l3IncomeSum={l3IncomeSum}
          l3ExpenseSum={l3ExpenseSum}
          l3SavingsSum={l3SavingsSum}
          l3NetSum={l3NetSum}
          loading={loading}
        />

        <BudgetVsActualMobileToggle>
          <BvaMTDCard bva={bvaCm} loading={loading} isMobile />
          <BvaL3Card  bva={bvaL3} loading={loading} isMobile />
        </BudgetVsActualMobileToggle>

        <BeatsMissesMobileToggle beats={beats} isMobile />

        <MoversMobileToggle movers={movers} isMobile />
      </div>

      {isMobile && (
        <div style={{ display: "grid", gap: "1rem", margin: "1rem 0" }}>
          <PieMobileCard
            incSlices={pieIncomeCM}
            expSlices={pieExpenseCM}
            savSlices={pieSavingsCM}
            isMobile
          />
        </div>
      )}

      <div style={isMobile ? gridStyleMobile : gridStyle}>
        {/* LEFT COLUMN — CURRENT MONTH */}
        <div style={colStyle}>
          {/* Net Summary — Current Month (desktop only) */}
          {!isMobile && (
            <NetSummaryMTD
              cmTotals={cmTotals}
              cmExpenseOnly={cmExpenseOnly}
              cmSavingsOnly={cmSavingsOnly}
              loading={loading}
            />
          )}

          {/* Budget vs Actual — Current Month (desktop only) */}
          {!isMobile && <BvaMTDCard bva={bvaCm} loading={loading} />}

          {/* Budget Beats — Current Month (desktop only, equal height with Top Movers — Favorably) */}
          {!isMobile && (
            <div ref={beatsRef}>
              <BeatsCard beats={beats} isMobile={false} stretch />
            </div>
          )}

          {/* Budget Misses — Current Month (desktop only, equal height with Top Movers — Unfavorably) */}
          {!isMobile && (
            <div ref={missesRef}>
              <MissesCard beats={beats} isMobile={false} stretch />
            </div>
          )}
        </div>

        {/* RIGHT COLUMN — LAST 3 FULL MONTHS */}
        <div style={colStyle}>
          {/* Net Summary — Last 3 Full Months (desktop only) */}
          {!isMobile && (
            <NetSummaryL3
              l3IncomeSum={l3IncomeSum}
              l3ExpenseSum={l3ExpenseSum}
              l3SavingsSum={l3SavingsSum}
              l3NetSum={l3NetSum}
              loading={loading}
            />
          )}

          {/* Budget vs Actual — Last 3 Full Months (desktop only) */}
          {!isMobile && <BvaL3Card bva={bvaL3} loading={loading} />}

          {/* Top Movers — Favorably (desktop only, equal height with Budget Beats) */}
          {!isMobile && (
            <div ref={moversUpRef}>
              <MoversUpCard movers={movers} isMobile={false} stretch />
            </div>
          )}

          {/* Top Movers — Unfavorably (desktop only, equal height with Budget Misses) */}
          {!isMobile && (
            <div ref={moversDownRef}>
              <MoversDownCard movers={movers} isMobile={false} stretch />
            </div>
          )}
        </div>

        {/* FULL-WIDTH: Current Month — Income, Expense, Savings (Three Pies in one card) */}
        {!isMobile && (
          <div className="dash-card" style={{ ...cardStyle, gridColumn: "1 / -1", margin: "1rem 0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
              <div style={{ fontSize: 14, color: "#666" }}>Breakdown by Section — Current Month</div>
              <TimeBadge basis="current" />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginTop: 8 }}>
              <div>
                <div style={{ fontSize: 12, color: "#006A4F", fontWeight: 700, margin: "4px 0 6px" }}>Income</div>
                <MiniPieChart
                  data={pieIncomeCM}
                  mode="income"
                  showLegend={true}
                  cxShift={90}           // you said +90 looks good on your screen
                  legendValueX={260}     // pushes $ further from labels
                  legendRowGap={20}
                  legendLabelMax={26}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, color: "#7D0016", fontWeight: 700, margin: "4px 0 6px" }}>Expenses</div>
                <MiniPieChart
                  data={pieExpenseCM}
                  mode="expense"
                  showLegend={true}
                  cxShift={90}
                  legendValueX={260}
                  legendRowGap={20}
                  legendLabelMax={26}
                />
              </div>

              <div>
                <div style={{ fontSize: 12, color: TRELLI_GOLD, fontWeight: 700, margin: "4px 0 6px" }}>Savings/Goals</div>
                <MiniPieChart
                  data={pieSavingsCM}
                  mode="savings"
                  showLegend={true}
                  cxShift={90}
                  legendValueX={260}
                  legendRowGap={20}
                  legendLabelMax={26}
                />
              </div>
            </div>
          </div>
        )}

        {/* Net by Month */}
        {isMobile ? (
          <NetByMonthMobileCard data={seriesL3Net} />
        ) : (
          <NetByMonthDesktopCard data={seriesL3Net} />
        )}

        {/* FULL-WIDTH: Income vs Expense — Bar (Last 3 Full Months) */}
        {isMobile ? (
          <IEBarMobileCard data={seriesL3IE} />
        ) : (
          <div className="dash-card" style={{ ...cardStyle, gridColumn: "1 / -1" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <div style={{ fontSize: 14, color: "#666" }}>Income vs Expense Comparison — Bar Graph</div>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, fontSize: 12, color: "#555" }}>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, background: TRELLI_GREEN, display: "inline-block", borderRadius: 2 }} />
                    Income
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, background: TRELLI_RED, display: "inline-block", borderRadius: 2 }} />
                    Expense
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, background: TRELLI_GOLD, display: "inline-block", borderRadius: 2 }} />
                    Savings/Goals
                  </span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <span style={{ width: 10, height: 10, background: "#111", display: "inline-block", borderRadius: 2 }} />
                    Net
                  </span>
                </div>
                <TimeBadge basis="last3" />
              </div>
            </div>
            <div style={{ marginTop: 8 }}>
              <MiniBarChart data={seriesL3IE} height={260} padding={28} fixedWidth={1200} />
            </div>
          </div>
        )}
      </div>
      
    </div>
  </ErrorCatcher>
  );
}